import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Tabs,
  TabList,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  Stack,
  Divider,
  useBreakpointValue,
  Input,
  Select,
  Textarea,
  Tooltip,
  Portal,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  RadioGroup,
  Radio,
  ButtonGroup,
  Button,
  useBoolean,
  Icon,
} from "@chakra-ui/react";
import {
  FaTwitter,
  FaRobot,
  FaScroll,
  FaSmile,
  FaEye,
  FaUndo,
  FaComments,
  FaHashtag,
  FaAt,
  FaFolder,
  FaPaintBrush,
  FaStream,
  FaImage,
  FaVideo,
  FaPaperclip,
  FaPlus,
  FaInfoCircle,
  FaCameraRetro,
} from "react-icons/fa";
import { cloneDeep, isEqual } from "lodash";
import DropTargetHandler from "../../../_plugins/uppy/drop-target-handler";

import * as PostEditorModes from "./PostEditorModes.constants";
import * as PostEditorActions from "./PostEditorActions.constants";
import * as PostEditorTabsTypes from "./PostEditorTabsTypes.constants";
import * as PostEditorQuickMediaTypes from "./PostEditorQuickMediaTypes.constants";
import * as PostEditorMediaSpotTypes from "./PostEditorMediaSpotTypes.constants";
import * as PostEditorErrorLevels from "./PostEditorErrorLevels.constants";
import * as PostEditorMediaTypes from "../PostMedia/PostMediaTypes.constants";
import * as PostAdditionalTypes from "../../../_constants/PostAdditionalTypes";
import * as PostTypes from "../../../_constants/PostTypes";
import {
  ADVANCED_AI_DOCUMENTS,
  ADVANCED_AI_EDITOR_PANEL,
  FeatureCodes,
} from "../../details/constants";
import SocialAccountPropType from "../../../_types/SocialAccount";
import PostPropType from "../../../_types/Post";

import { trackBasicEvent } from "../../../_utils/eventsTracker";
import {
  analyticsTrackEvent,
  isFeatureAvailable,
} from "../../../_utils/common";
import { getUrlWithProtocol } from "./utils";
import {
  getTabsForAccounts,
  getEntitiesMapForTab,
  getStateForTab,
  getAccountsForTab,
  getConfigForTab,
  postMediaToQuickMedia,
  isQuickMediaFileInProgress,
  isBaseTabEmpty,
  getCustomizedTimesByDelay,
  getMediaFileMeta,
  getPhotoVideoMaxOrder,
  photosVideosSortFunc,
  photosVideosDataSortFunc,
  getFixedPhotosVideosOrder,
} from "./helpers";
import {
  getTwitterMentions,
  getLinkedInMentions,
  getFacebookMentions,
  savePost,
  addSchedulePost,
  addBufferPost,
  addDraft,
  addForApprovalDraft,
  saveDraft,
  setMediaSidebarPanelOpen,
  addPublishNowPost,
} from "../../../_actions/plannerActions";
import {
  openAdvancedSettings,
  closeAdvancedSettings,
} from "../../../_actions/sidebarSliderActions";
import { getAdditionalTypesFields, getErrors } from "./restrictions";

import {
  tabsStateInit,
  tabsStateReducer,
} from "./postEditorTabsState.reducers";
import {
  tabsErrorsInit,
  tabsErrorsReducer,
} from "./postEditorTabsErrors.reducers";
import {
  quickMediaInit,
  quickMediaReducer,
} from "./postEditorQuickMedia.reducers";

import usePrevious from "../../../_hooks/usePrevious";
import useLocalStorage from "../../../_hooks/useLocalStorage";
import useSessionStorage from "../../../_hooks/useSessionStorage";
import useScraper from "../PostLinkPreview/useScraper";
import usePostFilePicker, {
  isPhotoFile,
  isVideoFile,
  getPhotoAllowedFileTypes,
  getPhotoMaxSize,
  getVideoAllowedFileTypes,
  getVideoMaxSize,
} from "../PostFilePicker/usePostFilePicker";

import { extractLinks } from "../../../_plugins/prosemirror/link-highlighter-plugin";
import { useTranslation } from "react-i18next";
import EmojiPicker from "../EmojiPicker/EmojiPicker";
import SnippetsPicker from "../SnippetsPicker/SnippetsPicker";
import PostLinkPreview from "../PostLinkPreview/PostLinkPreview";
import PostCarousel from "../PostCarousel/PostCarousel";
import PostFilePickerModal from "../PostFilePicker/PostFilePickerModal";
import AdvancedMessageField, {
  replaceLinks,
} from "../AdvancedMessageField/AdvancedMessageField";
import AdvancedMessageToolbarButton from "../AdvancedMessageField/AdvancedMessageToolbarButton";
import {
  AdvancedMessageAtMentionsCounter,
  AdvancedMessageCharsCounter,
  AdvancedMessageCharsTwitterCounter,
  AdvancedMessageHashtagsCounter,
  AdvancedMessageMentionsCounter,
} from "../AdvancedMessageField/AdvancedMessageCounter";
import TldrMediaSidebarPanel from "../../../TldrCalendarPlanner/Components/TldrMediaSidebar/TldrMediaSidebarPanel";
import StudioBaseRightSideBar from "../../details/advanced/common/StudioBaseRightSideBar";
import TldrStudioAiPanel from "../../common/TldrStudioAiPanel";
import ConfirmDangerButton from "../ConfirmDanger/ConfirmDangerButton";
import PostMediaList from "../PostMedia/PostMediaList";
import PostMediaListItem from "../PostMedia/PostMediaListItem";
import PostMediaPreviewModal from "../PostMedia/PostMediaPreviewModal";
import PostMediaEditThumbModal from "../PostMedia/PostMediaEditThumbModal";
// import ImageEditorModal from '../ImageEditor/ImageEditorModal';
import PostPreviewModal from "../PostPreview/PostPreviewModal";
import PostEditorTab from "./PostEditorTab";
import PostEditorError from "./PostEditorError";
import PostEditorAddDraftTimePickerModal from "./PostEditorAddDraftTimePickerModal";
import PostEditorScheduleTimePickerModal from "./PostEditorScheduleTimePickerModal";
import PostEditorAddToQueueModal from "./PostEditorAddToQueueModal";
import PostEditorPublishNowConfirmModal from "./PostEditorPublishNowConfirmModal";
import PostEditorCreatePostButtons from "./PostEditorCreatePostButtons";
import PostEditorEditPostButtons from "./PostEditorEditPostButtons";
import PostEditorCreateDraftPostButtons from "./PostEditorCreateDraftPostButtons";
import PostEditorEditDraftPostButtons from "./PostEditorEditDraftPostButtons";
import Preloader from "../Preloader/Preloader";
import PreloaderWrapper from "../Preloader/PreloaderWrapper";
import PostEditCommentsModal from "../PostEditComments/PostEditCommentsModal";
import DateTimePicker from "../DateTimePicker/DateTimePicker";
import useAppFeatures from "../../../_hooks/useAppFeatures";
import useUserSettings from "../../../_hooks/useUserSettings";
import TldrUpgradeSubscriptionModal from "../../common/modals/TldrUpgradeSubscriptionModal";
import { showToast } from "../../../_utils/showToast";
import { getExtension } from "../../../_utils/mimeType";
// import { openNewAiDocumentInPanel, updateDocumentData } from "../../../_actions/aiDocumentActions";
import SnapshotMakerModal from "../SnapshotMaker/SnapshotMakerModal";

const wrapperStyles = {
  scrollMarginTop: "var(--sticky-top-offset, 0px)",
};

const dropTargetStyles = {
  position: "relative",
  "&.uppy-is-drag-over::after": {
    position: "absolute",
    inset: "-3px",
    zIndex: 10000,
    backgroundColor: "blackAlpha.500",
    border: "3px dashed",
    borderColor: "whiteAlpha.700",
    content: '""',
  },
};

const contentWrapperStyles = {
  position: "relative",
  background: "gray.600",
  paddingBottom: "1",
  marginBottom: "3",
};

const messageStyles = {};

const errorsStyles = {
  paddingBottom: "1",
};

const mediaStyles = {
  paddingX: "2",
  paddingY: "1",
  _empty: {
    display: "none",
  },
};

const toolbarStyles = {
  display: "flex",
  flexDirection: "row-reverse",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "1",
};

const customFieldsStyles = {
  paddingX: "3",
  paddingY: "1",
  marginBottom: "1",
  borderWidth: "1px 0",
  borderStyle: "solid",
  borderColor: "whiteAlpha.100",
};

const customFieldRowStyles = {
  display: "flex",
  alignItems: "center",
  maxWidth: "40rem",
  marginTop: "1",
  marginBottom: "1",
};

const customFieldLabelStyles = {
  flex: "0 0 auto",
  width: "25%",
  minWidth: "7rem",
  paddingRight: "1",
  fontSize: "sm",
  lineHeight: "1.1",
};

const customFieldContentStyles = {
  flex: "1 1 auto",
  display: "flex",
  alignItems: "center",
};

const sidebarPortal = (children) => (
  <Portal>
    <Box sx={{ zIndex: "modal", position: "relative" }}>{children}</Box>
  </Portal>
);

const generateGroupId = (workspaceId, userId) =>
  `pe-${workspaceId}-${userId}-${new Date().getTime()}`;

const PostEditor = (
  {
    accounts,
    accountsIds,
    canEditDrafts,
    canEditPosts,
    postData,
    addPostMedia,
    mode = PostEditorModes.CREATE,
    dataStoreKey,
    customHideTimeSelector,
    onLoading,
    onActionDone,
    onCustomAction,
  },
) => {
  const { t } = useTranslation(["planner", "common", "accounts"]);
  const editorRef = useRef(null);
  const contentRef = useRef(null);
  const messageRef = useRef(null);
  const addMediaButtonRef = useRef(null);
  const [appFeatures] = useAppFeatures();
  const [userSettings] = useUserSettings();
  const [workspaceFeatures, setWorkspaceFeatures] = useState({
    shortlinks: false,
    video: false,
    carousel: false,
    comments: false,
    snippets: false,
    mentions: false,
    canAddSchedule: false,
    canAddBuffer: false,
    canAddDraft: false,
  });
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const hideImageEditor = true; // useBreakpointValue({ base: true, md: false }); @NOTE Image editor disabled
  const [reloadMessageEditor, setReloadMessageEditor] = useReducer(
    (state, action) => (action ? state + 1 : 0),
    0
  );
  const [reloadLinks, setReloadLinks] = useReducer(
    (state, action) => (action ? state + 1 : 0),
    0
  );
  const [addToQueueAcknowledged, setAddToQueueAcknowledged] = useLocalStorage(
    "addToQueueAcknowledged",
    false
  );
  const [messageFocused, setMessageFocused] = useState();
  const [messagePasted, setMessagePasted] = useState(false);
  const [activeTabs, setActiveTabs] = useState([PostEditorTabsTypes.BASE]);
  const prevActiveTabs = usePrevious(activeTabs);
  const [activeTab, setActiveTab] = useState(PostEditorTabsTypes.BASE);
  const { fetchScraperData } = useScraper();
  const [savedData, setSavedData] = useSessionStorage(dataStoreKey);

  const [previewMediaFile, setPreviewMediaFile] = useState(null);
  const [editThumbMediaFile, setEditThumbMediaFile] = useState(null);
  const [imageEditorFile, setImageEditorFile] = useState(null);
  const [imageEditorOpen, setImageEditorOpen] = useState();
  const [quickMedia, dispatchQuickMedia] = useReducer(
    quickMediaReducer,
    [],
    quickMediaInit
  );
  const [uploadInProgress, setUploadInProgress] = useState();
  const [lastUploadedFile, setLastUploadedFile] = useState(null);
  const [filesToAdd, setFilesToAdd] = useState(null);
  const [filePickerType, setFilePickerType] = useState(null);
  const [filePickerOpen, setFilePickerOpen] = useState();
  const [droppedFiles, setDroppedFiles] = useState(null);
  const { uppy, uppyPlugins, uppyNote, setUppyOptions } = usePostFilePicker({
    id: "post-editor",
  });

  const [postId, setPostId] = useState(null);
  const [timeState, setTimeState] = useState({});
  const [customizedTimesState, setCustomizedTimesState] = useState(null);
  const [delayTimeState, setDelayTimeState] = useState(null);
  const [shortlinksData, setShortlinksData] = useState({});
  const [tabsState, dispatchTabsState] = useReducer(
    tabsStateReducer,
    {},
    tabsStateInit
  );
  const [tabsErrors, dispatchTabsErrors] = useReducer(
    tabsErrorsReducer,
    {},
    tabsErrorsInit
  );
  const [messageLinkPreview, setMessageLinkPreview] = useState(null);
  const [editComments, setEditComments] = useState(null);
  const [showMediaClipboard, setShowMediaClipboard] = useState(true);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(null);
  const [postPreview, setPostPreview] = useState(null);
  const [snapshotMaker, setSnapshotMaker] = useState(null);
  const [loading, setLoading] = useState(null);
  const [savingInProgress, setSavingInProgress] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState({
    open: false,
    title: undefined,
  });
  const [snippetsOpen, setSnippetsOpen] = useBoolean();
  const [snippetData, setSnippetData] = useState();

  const dispatch = useDispatch();

  const mediaSidebarPanelOpen = useSelector(
    (state) => state.plannerStore.mediaSidebarPanelOpen
  );
  const rightSidebarPanel = useSelector((state) => state.rightsidebar);

  const userId = useSelector((state) => state.auth.payload?.user?.pk);
  const subscriptionData = useSelector((state) => state.subscription);
  const workspace = useSelector((state) =>
    state.auth.payload?.orgs?.find(
      (item) => item.id === state.auth.payload?.selectedOrg
    )
  );
  const defaultTimezone = useMemo(
    () => workspace?.settings?.timezone,
    [workspace]
  );

  const singleMode = useMemo(
    () =>
      [
        PostEditorModes.EDIT,
        PostEditorModes.DRAFT_EDIT,
      ].includes(mode),
    [mode]
  );
  const baseTabState = useMemo(
    () => tabsState[PostEditorTabsTypes.BASE],
    [tabsState]
  );
  const activeTabIndex = useMemo(
    () => activeTabs.indexOf(activeTab),
    [activeTab, activeTabs]
  );
  const activeTabState = useMemo(
    () => tabsState[activeTab] || getStateForTab(baseTabState, activeTab),
    [activeTab, tabsState, baseTabState]
  );
  const activeTabErrors = useMemo(
    () => tabsErrors[activeTab] || [],
    [activeTab, tabsErrors]
  );
  const activeTabConfig = useMemo(
    () => {
      const postType = activeTabState?.additional?.find(
        (item) => item.type === PostAdditionalTypes.POST_TYPE
      )?.value;

      return getConfigForTab(activeTab, postType, activeTabs);
    },
    [activeTab, activeTabs, activeTabState?.additional]
  );
  const activeTabMedia = useMemo(() => {
    const videos =
      activeTabState?.videos
        ?.map((item) =>
          item.uploadId
            ? {
              ...quickMedia.find((qItem) => qItem.id === item.uploadId),
              data: item,
            }
            : postMediaToQuickMedia(PostEditorQuickMediaTypes.VIDEO, item)
        )
        ?.filter((item) => item?.id) || [];
    const photos =
      activeTabState?.photos
        ?.map((item) =>
          item.uploadId
            ? {
              ...quickMedia.find((qItem) => qItem.id === item.uploadId),
              data: item,
            }
            : postMediaToQuickMedia(PostEditorQuickMediaTypes.PHOTO, item)
        )
        ?.filter((item) => item?.id) || [];

    return [...videos, ...photos].sort(photosVideosDataSortFunc);
  }, [quickMedia, activeTabState]);
  const activeTabQuickMedia = useMemo(
    () =>
      quickMedia.filter(
        (item) =>
          !activeTabState.videos.find(
            (video) =>
              (item.url && video.url === item.url) ||
              (item.id && video.uploadId === item.id)
          ) &&
          !activeTabState.photos.find(
            (photo) =>
              (item.url && photo.url === item.url) ||
              (item.id && photo.uploadId === item.id)
          ) &&
          item.type !== PostEditorQuickMediaTypes.PHOTO_URL // @NOTE disable adding files from URL - require backend changes first
      ),
    [quickMedia, activeTabState]
  );
  const activeTabAdditional = useMemo(
    () => {
      const postType = activeTabState?.additional?.find(
        (item) => item.type === PostAdditionalTypes.POST_TYPE
      )?.value;

      return getAdditionalTypesFields(activeTab, postType, activeTabState?.additional);
    },
    [activeTab, activeTabState?.additional]
  );

  const openUpgradeModal = useCallback(
    (title) => {
      setUpgradeModal({
        open: true,
        title,
      });
    },
    [setUpgradeModal]
  );

  const closeUpgradeModal = useCallback(() => {
    setUpgradeModal({
      open: false,
      title: undefined,
    });
  }, [setUpgradeModal]);

  const exportPostData = useCallback(
    (options = {}) => {
      const groupId = generateGroupId(workspace?.id, userId);
      const postsByProvider = {};
      const tabs = singleMode ? [activeTabs[1]] : activeTabs;
      let customizedTimes = null;

      if (customizedTimesState) {
        customizedTimes = {
          ...customizedTimesState,
        };
      } else if (delayTimeState) {
        const sortedAccountsIds = accounts
          .filter((item) => accountsIds.includes(item.id))
          .map((item) => item.id);

        customizedTimes = {
          ...getCustomizedTimesByDelay(
            sortedAccountsIds,
            timeState.date,
            timeState.time,
            timeState.timezone,
            delayTimeState?.unit,
            delayTimeState?.value
          ),
        };
      }

      tabs.forEach((tab) => {
        if (tab !== PostEditorTabsTypes.BASE) {
          const { ...providerPostData } =
            tabsState[tab] || getStateForTab(baseTabState, tab);
          const providerAccounts = getAccountsForTab(
            accounts,
            accountsIds,
            tab
          );
          const providerAccountsIds = providerAccounts.map((item) => item.id);

          postsByProvider[tab] = {
            accounts: providerAccountsIds,
            ...timeState,
            ...providerPostData,
            shortlinksUtmData: {
              shortlinksEnabled: false,
              utmEnabled: false,
              shortLinks: cloneDeep(shortlinksData),
            },
          };

          if (customizedTimes) {
            postsByProvider[tab].customizeDate = {};

            Object.keys(customizedTimes).forEach((id) => {
              if (providerAccountsIds.includes(id)) {
                postsByProvider[tab].customizeDate[id] = customizedTimes[id];
              }
            });
          }

          if (postData?.draftId) {
            postsByProvider[tab].draftId = postData.draftId;
          } else if (mode === PostEditorModes.DRAFT_EDIT && postId) {
            postsByProvider[tab].draftId = postId;
          }

          if (options?.addGroupId) {
            postsByProvider[tab].groupId = groupId;
          }

          // cleanup generated $id required for view
          if (postsByProvider[tab]?.additional) {
            postsByProvider[tab].additional = postsByProvider[
              tab
            ].additional.map((typeItem) => {
              if (typeItem.type === PostAdditionalTypes.CAROUSEL) {
                return {
                  ...typeItem,
                  items: typeItem.items.map(({ $id, ...item }) => item),
                };
              }

              return typeItem;
            });
          }

          delete postsByProvider[tab]._scraperImageUploadId;
        }
      });

      return postsByProvider;
    },
    [
      accounts,
      accountsIds,
      activeTabs,
      tabsState,
      baseTabState,
      timeState,
      shortlinksData,
      customizedTimesState,
      delayTimeState,
      singleMode,
      postData,
      workspace?.id,
      userId,
      mode,
      postId,
    ]
  );

  const scrollToErrors = useCallback(() => {
    editorRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [editorRef]);

  const updateMessageEditor = useCallback(() => {
    messageRef.current?.update();
  }, [messageRef]);

  const setEditorData = useCallback(
    async (data = {}, restoreData = undefined) => {
      setLoading(true);

      // add order for photos/videos if not set and sort array items by order
      if (data?.photos || data?.videos) {
        const lastOrder = getPhotoVideoMaxOrder(data?.photos, data?.videos);
        let orderCounter = 0;

        data.videos = (data.videos || [])
          .map((item) => {
            if (item.order >= 0) {
              return item;
            }

            orderCounter = orderCounter + 1;

            return {
              ...item,
              order: lastOrder + orderCounter,
            };
          })
          .sort(photosVideosSortFunc);

        data.photos = (data.photos || [])
          .map((item) => {
            if (item.order >= 0) {
              return item;
            }

            orderCounter = orderCounter + 1;

            return {
              ...item,
              order: lastOrder + orderCounter,
            };
          })
          .sort(photosVideosSortFunc);
      }

      const shortlinksUtmData = data?.shortlinksUtmData || {};
      const videos =
        data?.videos?.map((item) =>
          postMediaToQuickMedia(PostEditorQuickMediaTypes.VIDEO, item)
        ) || [];
      const photos =
        data?.photos?.map((item) =>
          postMediaToQuickMedia(PostEditorQuickMediaTypes.PHOTO, item)
        ) || [];
      const linkPhotos = data?.scraper?.image
        ? [
          {
            id: data.scraper.image,
            type: PostEditorQuickMediaTypes.PHOTO_URL,
            url: data.scraper.image,
            thumbUrl: data.scraper.image,
          },
        ]
        : [];

      let tabsStatePayload = {
        [PostEditorTabsTypes.BASE]: {
          ...cloneDeep(data),
        },
      };
      let quickMediaPayload = [...videos, ...photos, ...linkPhotos];
      let shortlinksUtmDataPayload = {
        ...shortlinksUtmData?.shortLinks,
      };

      if (!Object.keys(data).length && restoreData) {
        const restoredTabsStates = {};

        if (restoreData?.tabsState) {
          Object.keys(restoreData.tabsState).forEach((tabKey) => {
            const tabData = restoreData.tabsState[tabKey];

            if (tabData) {
              if (tabData?.videos) {
                tabData.videos =
                  tabData.videos?.filter(
                    (item) => item && !isQuickMediaFileInProgress(item)
                  ) || [];
              }

              if (tabData?.photos) {
                tabData.photos =
                  tabData.photos?.filter(
                    (item) => item && !isQuickMediaFileInProgress(item)
                  ) || [];
              }

              if (tabData?._scraperImageUploadId) {
                delete tabData._scraperImageUploadId;
              }

              restoredTabsStates[tabKey] = cloneDeep(tabData);
            }
          });
        }

        tabsStatePayload = {
          ...tabsStatePayload,
          ...restoredTabsStates,
        };

        quickMediaPayload = [
          ...cloneDeep(
            savedData?.quickMedia?.filter(
              (item) => item && item.id && !isQuickMediaFileInProgress(item)
            ) || []
          ),
        ];

        shortlinksUtmDataPayload = {
          ...cloneDeep(savedData?.shortlinksData || {}),
        };
      }

      // add meta for media files
      await (async () => {
        for (const tab in tabsStatePayload) {
          const tabData = tabsStatePayload[tab];

          if (tabData?.videos?.length) {
            tabData.videos = await Promise.all(
              tabData.videos.map(async (item) => {
                if (!item.meta) {
                  return {
                    ...item,
                    meta: await getMediaFileMeta("video", item.url),
                  };
                }

                return item;
              })
            );
          }

          if (tabData?.photos?.length) {
            tabData.photos = await Promise.all(
              tabData.photos.map(async (item) => {
                if (!item.meta) {
                  return {
                    ...item,
                    meta: await getMediaFileMeta("image", item.url),
                  };
                }

                return item;
              })
            );
          }
        }
      })();
      quickMediaPayload = await Promise.all(
        quickMediaPayload.map(async (item) => {
          if (item.data && !item.data.meta) {
            let meta;

            if (item.type === PostEditorQuickMediaTypes.VIDEO) {
              meta = await getMediaFileMeta("video", item.data.url);
            } else if (
              [
                PostEditorQuickMediaTypes.PHOTO,
                PostEditorQuickMediaTypes.PHOTO_URL,
              ].includes(item.type)
            ) {
              meta = await getMediaFileMeta("image", item.data.url);
            }

            if (meta) {
              return {
                ...item,
                data: {
                  ...item.data,
                  meta,
                },
              };
            }
          }

          return item;
        })
      );

      setPostId(data?.id || null);
      setTimeState({
        time: data?.time,
        date: data?.date,
        timezone: data?.timezone,
      });
      setCustomizedTimesState(null);
      setDelayTimeState(null);
      setShortlinksData(shortlinksUtmDataPayload);
      dispatchTabsState({
        type: "init",
        payload: tabsStatePayload,
      });
      dispatchTabsErrors({
        type: "init",
        payload: {},
      });
      dispatchQuickMedia({
        type: "init",
        payload: quickMediaPayload,
      });

      if (!singleMode && activeTab !== PostEditorTabsTypes.BASE) {
        if (
          !activeTabs.includes(activeTab) ||
          activeTabs?.length !== 2 ||
          !isBaseTabEmpty(tabsStatePayload[PostEditorTabsTypes.BASE])
        ) {
          setActiveTab(PostEditorTabsTypes.BASE);
        }
      }

      setLoading(false);
      setReloadMessageEditor(true);
    },
    [
      setLoading,
      setReloadMessageEditor,
      setPostId,
      setTimeState,
      setCustomizedTimesState,
      setDelayTimeState,
      setShortlinksData,
      dispatchTabsState,
      dispatchTabsErrors,
      dispatchQuickMedia,
      setActiveTab,
      activeTab,
      activeTabs,
      singleMode,
      savedData,
    ]
  );

  const getTabErrors = useCallback(
    async (tab) => {
      if (tab !== PostEditorTabsTypes.BASE) {
        const tabPost = tabsState[tab] || getStateForTab(baseTabState, tab);
        const tabAccounts = getAccountsForTab(accounts, accountsIds, tab);
        const postType = tabPost.additional?.find(
          (item) => item.type === PostAdditionalTypes.POST_TYPE
        )?.value;
        const errors = await getErrors(tab, postType, tabAccounts, tabPost, {
          connectedDevices: userSettings.connectedDevices,
        });

        return errors;
      }

      return null;
    },
    [
      baseTabState,
      tabsState,
      accounts,
      accountsIds,
      userSettings?.connectedDevices,
    ]
  );

  const validateTabs = useCallback(async () => {
    const errorsByTab = {};

    await Promise.all(
      activeTabs.map(async (tab) => {
        if (tab !== PostEditorTabsTypes.BASE) {
          const errors = await getTabErrors(tab);

          if (errors?.length) {
            errorsByTab[tab] = errors;
          }
        }
      })
    );

    dispatchTabsErrors({
      type: "init",
      payload: errorsByTab,
    });

    return errorsByTab;
  }, [dispatchTabsErrors, getTabErrors, activeTabs]);

  const handleMentionGetItems = useCallback(
    async (text, done) => {
      let response = {};

      switch (activeTab) {
        case PostEditorTabsTypes.TWITTER: {
          response = await dispatch(getTwitterMentions(text));
          break;
        }
        case PostEditorTabsTypes.LINKEDIN: {
          response = await dispatch(getLinkedInMentions(text));
          break;
        }
        case PostEditorTabsTypes.FACEBOOK: {
          response = await dispatch(getFacebookMentions(text));
          break;
        }
        default: {
          // nothing
        }
      }

      if (response?.data?.status === 1) {
        done(response.data.items || []);
      } else {
        done([]);
      }
    },
    [activeTab, dispatch]
  );

  const handleMentionSelect = useCallback(() => {
    trackBasicEvent("post-editor", "click", "mention - use");
  }, []);

  const insertTextToMessage = useCallback(
    (text) => {
      const view = messageRef.current?.view;

      if (!view) return;

      view.dispatch(view.state.tr.insertText(text));
    },
    [messageRef]
  );

  const handleTimeChange = useCallback(
    (values) => {
      setTimeState({
        date: values.date,
        time: values.time,
        timezone: values.timezone,
      });
    },
    [setTimeState]
  );

  const handleDelayTimeChange = useCallback(
    (values) => {
      if (values) {
        setDelayTimeState({
          value: values.value,
          unit: values.unit,
        });
      } else {
        setDelayTimeState(null);
      }
    },
    [setDelayTimeState]
  );

  const handleCustomizedTimesChange = useCallback(
    (values) => {
      if (values) {
        setCustomizedTimesState(cloneDeep(values));
      } else {
        setCustomizedTimesState(null);
      }
    },
    [setCustomizedTimesState]
  );

  const handleEmojiSelect = useCallback(
    (emoji) => {
      insertTextToMessage(emoji.native);

      trackBasicEvent("post-editor", "click", "emoji - use");
    },
    [insertTextToMessage]
  );

  const handleSnippetSelect = useCallback(
    (snippet) => {
      insertTextToMessage(snippet.text);

      trackBasicEvent("post-editor", "click", "snippet - use");
    },
    [insertTextToMessage]
  );

  const updateUploadedFilesIds = useCallback(() => {
    quickMedia.forEach((item) => {
      if (!isQuickMediaFileInProgress(item) && item.id !== item.url) {
        dispatchQuickMedia({
          type: "update",
          payload: {
            id: item.id,
            item: {
              id: item.url,
            },
          },
        });
      }
    });
  }, [dispatchQuickMedia, quickMedia]);

  const updateUploadedFilesInTabsState = useCallback(
    (allUploaded = false) => {
      const getItems = (items = []) =>
        items
          .map((item) => {
            if (item.uploadId) {
              return {
                ...(quickMedia.find((qItem) => qItem.id === item.uploadId)
                  ?.data || item),
                order: item.order,
              };
            }

            return item;
          })
          .filter((item) => item && (!allUploaded || !item.uploadId));
      const getScraper = (data, uploadId) => {
        if (data && uploadId) {
          const image =
            quickMedia.find((qItem) => qItem.id === uploadId)?.url ||
            data.image;

          return {
            ...data,
            image,
          };
        }

        return data || null;
      };

      Object.keys(tabsState).forEach((tab) => {
        dispatchTabsState({
          type: "merge",
          tab,
          payload: {
            videos: getItems(tabsState[tab]?.videos),
            photos: getItems(tabsState[tab]?.photos),
            scraper: getScraper(
              tabsState[tab]?.scraper,
              tabsState[tab]?._scraperImageUploadId
            ),
            _scraperImageUploadId: null,
          },
        });
      });
    },
    [dispatchTabsState, tabsState, quickMedia]
  );

  const addQuickMediaFiles = useCallback(
    (tab, spot, fileIds, allowPhotosAndVideos = false) => {
      const newItems = quickMedia.filter(
        (item) => item && fileIds.includes(item.id)
      );

      if (!newItems.length) return;

      const payload = {};
      const tabState = tabsState[tab] || getStateForTab(baseTabState, tab);

      const lastOrder = getPhotoVideoMaxOrder(
        tabState?.photos,
        tabState?.videos
      );
      let orderCounter = 0;

      if (spot === PostEditorMediaSpotTypes.VIDEOS) {
        const videos = [
          ...(tabState?.videos || []),
          ...newItems
            .map((item) => {
              if (isQuickMediaFileInProgress(item)) {
                orderCounter = orderCounter + 1;

                return {
                  uploadId: item.id,
                  order: lastOrder + orderCounter,
                };
              }

              if (item.type === PostEditorQuickMediaTypes.VIDEO) {
                orderCounter = orderCounter + 1;

                return {
                  ...item.data,
                  order: lastOrder + orderCounter,
                };
              }

              return null;
            })
            .filter((item) => !!item)
            .sort(photosVideosSortFunc),
        ];

        payload.photos = allowPhotosAndVideos
          ? [...(tabState?.photos || [])]
          : [];
        payload.videos = videos;
        payload.scraper = null;
        payload._scraperImageUploadId = null;
      } else if (spot === PostEditorMediaSpotTypes.PHOTOS) {
        const photos = [
          ...(tabState?.photos || []),
          ...newItems
            .map((item) => {
              if (isQuickMediaFileInProgress(item)) {
                orderCounter = orderCounter + 1;

                return {
                  uploadId: item.id,
                  order: lastOrder + orderCounter,
                };
              }

              if (item.type === PostEditorQuickMediaTypes.PHOTO) {
                orderCounter = orderCounter + 1;

                return {
                  ...item.data,
                  order: lastOrder + orderCounter,
                };
              }

              // if (item.type === PostEditorQuickMediaTypes.PHOTO_URL) {
              //   orderCounter = orderCounter + 1;
              //
              //   return {
              //     dir: item.url,
              //     fileName: item.url,
              //     url: item.url,
              //     thumbUrl: item.thumbUrl,
              //     meta: await getMediaFileMeta("image", item.url),
              //     order: lastOrder + orderCounter,
              //   };
              // }

              return null;
            })
            .filter((item) => !!item)
            .sort(photosVideosSortFunc),
        ];

        payload.photos = photos;
        payload.videos = allowPhotosAndVideos
          ? [...(tabState?.videos || [])]
          : [];
        payload.scraper = null;
        payload._scraperImageUploadId = null;
      } else if (spot === PostEditorMediaSpotTypes.LINK_PHOTO) {
        if (tabState?.scraper?.link) {
          const newItem = newItems[newItems.length - 1];

          if (isQuickMediaFileInProgress(newItem)) {
            payload._scraperImageUploadId = newItem.id;
          } else {
            payload.scraper = {
              ...tabState.scraper,
              image: newItem.url,
            };
          }
        }
      }

      if (payload.photos || payload.videos) {
        const fixedItems = getFixedPhotosVideosOrder(
          payload.photos,
          payload.videos
        );

        payload.photos = fixedItems.photos;
        payload.videos = fixedItems.videos;
      }

      if (payload && Object.keys(payload).length) {
        dispatchTabsState({
          type: "merge",
          tab,
          payload,
        });
      }
    },
    [dispatchTabsState, quickMedia, tabsState, baseTabState]
  );

  const handleCloseFilePicker = useCallback(() => {
    setFilePickerOpen(false);
    setFilePickerType(null);
  }, [setFilePickerOpen, setFilePickerType]);

  const handleAddImages = useCallback(() => {
    if (
      activeTabConfig.maxPhotosVideos >= 0 &&
      (activeTabState?.videos?.length || 0) +
      (activeTabState?.photos?.length || 0) >=
      activeTabConfig.maxPhotosVideos
    ) {
      showToast({
        description: t("post_editor.errors.photo_video_limit_reached", {
          max: activeTabConfig.maxPhotosVideos,
        }),
        title: "Error",
        status: "error",
      });
    } else if (activeTabState?.photos?.length >= activeTabConfig.maxPhotos) {
      showToast({
        description: t("post_editor.errors.photo_limit_reached", {
          max: activeTabConfig.maxPhotos,
        }),
        title: "Error",
        status: "error",
      });
    } else {
      setFilePickerType(PostEditorMediaSpotTypes.PHOTOS);
      setFilePickerOpen(true);

      trackBasicEvent("post-editor", "click", "media - add photos");
    }
  }, [
    setFilePickerOpen,
    setFilePickerType,
    activeTabState,
    activeTabConfig,
    t,
  ]);

  const handleMediaClipboardToggle = useCallback(() => {
    setShowMediaClipboard(!showMediaClipboard);
  }, [setShowMediaClipboard, showMediaClipboard]);

  const handleMediaSidebarPanelToggle = useCallback(() => {
    dispatch(setMediaSidebarPanelOpen(!mediaSidebarPanelOpen));
    analyticsTrackEvent(
      mediaSidebarPanelOpen
        ? "plannerMediaPanelClose"
        : "plannerMediaPanelOpen"
    );
  }, [mediaSidebarPanelOpen, dispatch]);

  const handleAiAssistantPanelToggle = useCallback(() => {
    if (rightSidebarPanel.isActionPanelOpen) {
      dispatch(closeAdvancedSettings());
      analyticsTrackEvent("plannerAIAssistantClose");
    } else {
      dispatch(
        openAdvancedSettings(ADVANCED_AI_EDITOR_PANEL, ADVANCED_AI_DOCUMENTS)
      );
      analyticsTrackEvent("plannerAIAssistantOpen");
    }
  }, [rightSidebarPanel.isActionPanelOpen, dispatch]);

  const handleAddVideos = useCallback(() => {
    if (!workspaceFeatures.video) {
      openUpgradeModal();
      return;
    }

    if (
      activeTabConfig.maxPhotosVideos >= 0 &&
      (activeTabState?.videos?.length || 0) +
      (activeTabState?.photos?.length || 0) >=
      activeTabConfig.maxPhotosVideos
    ) {
      showToast({
        description: t("post_editor.errors.photo_video_limit_reached", {
          max: activeTabConfig.maxPhotosVideos,
        }),
        title: "Error",
        status: "error",
      });
    } else if (activeTabState?.videos?.length >= activeTabConfig.maxVideos) {
      showToast({
        description: t("post_editor.errors.video_limit_reached", {
          max: activeTabConfig.maxVideos,
        }),
        title: "Error",
        status: "error",
      });
    } else {
      setFilePickerType(PostEditorMediaSpotTypes.VIDEOS);
      setFilePickerOpen(true);

      trackBasicEvent("post-editor", "click", "media - add videos");
    }
  }, [
    setFilePickerOpen,
    setFilePickerType,
    openUpgradeModal,
    activeTabState,
    activeTabConfig,
    workspaceFeatures,
    t,
  ]);

  const handleAddPhotoWithImageEditor = useCallback(() => {
    if (
      activeTabConfig.maxPhotosVideos >= 0 &&
      (activeTabState?.videos?.length || 0) +
      (activeTabState?.photos?.length || 0) >=
      activeTabConfig.maxPhotosVideos
    ) {
      showToast({
        description: t("post_editor.errors.photo_video_limit_reached", {
          max: activeTabConfig.maxPhotosVideos,
        }),
        title: "Error",
        status: "error",
      });
    } else if (activeTabState?.photos?.length >= activeTabConfig.maxPhotos) {
      showToast({
        description: t("post_editor.errors.photo_limit_reached", {
          max: activeTabConfig.maxPhotos,
        }),
        title: "Error",
        status: "error",
      });
    } else {
      setFilePickerType(PostEditorMediaSpotTypes.PHOTOS);
      setImageEditorFile(null);
      setImageEditorOpen(true);

      trackBasicEvent(
        "post-editor",
        "click",
        "image editor - create new file"
      );
    }
  }, [
    setImageEditorOpen,
    setImageEditorFile,
    setFilePickerType,
    activeTabState,
    activeTabConfig,
    t,
  ]);

  const handleRemoveQuickMediaFile = useCallback(
    ({ id }) => {
      uppy.removeFile(id);

      dispatchQuickMedia({
        type: "remove",
        payload: { id },
      });

      trackBasicEvent("post-editor", "click", "media - remove");
    },
    [dispatchQuickMedia, uppy]
  );

  const handleUseQuickMediaFile = useCallback(
    ({ id, type }) => {
      if (
        activeTabConfig.maxPhotosVideos >= 0 &&
        (activeTabState?.videos?.length || 0) +
        (activeTabState?.photos?.length || 0) >=
        activeTabConfig.maxPhotosVideos
      ) {
        showToast({
          description: t("post_editor.errors.photo_video_limit_reached", {
            max: activeTabConfig.maxPhotosVideos,
          }),
          title: "Error",
          status: "error",
        });
      } else if (type === PostEditorMediaTypes.VIDEO) {
        if (activeTabState?.videos?.length >= activeTabConfig.maxVideos) {
          showToast({
            description: t("post_editor.errors.video_limit_reached", {
              max: activeTabConfig.maxVideos,
            }),
            title: "Error",
            status: "error",
          });
        } else {
          addQuickMediaFiles(
            activeTab,
            PostEditorMediaSpotTypes.VIDEOS,
            [id],
            activeTabConfig.maxPhotosVideos >= 0
          );
        }
      } else if (type === PostEditorMediaTypes.PHOTO) {
        if (activeTabState?.photos?.length >= activeTabConfig.maxPhotos) {
          showToast({
            description: t("post_editor.errors.photo_limit_reached", {
              max: activeTabConfig.maxPhotos,
            }),
            title: "Error",
            status: "error",
          });
        } else {
          addQuickMediaFiles(
            activeTab,
            PostEditorMediaSpotTypes.PHOTOS,
            [id],
            activeTabConfig.maxPhotosVideos >= 0
          );
        }
      }

      trackBasicEvent("post-editor", "click", "media - use");
    },
    [addQuickMediaFiles, activeTabState, activeTab, activeTabConfig, t]
  );

  const handleRemoveMediaFile = useCallback(
    ({ id, url }) => {
      dispatchTabsState({
        type: "merge",
        tab: activeTab,
        payload: {
          videos: activeTabState.videos.filter(
            (video) =>
              (!id || video.uploadId !== id) && (!url || video.url !== url)
          ),
          photos: activeTabState.photos.filter(
            (photo) =>
              (!id || photo.uploadId !== id) && (!url || photo.url !== url)
          ),
        },
      });

      trackBasicEvent("post-editor", "click", "media - remove as used");
    },
    [dispatchTabsState, activeTabState, activeTab]
  );

  const handleEditThumbMediaFile = useCallback(
    ({ id, type, url, data }) => {
      if (type !== PostEditorMediaTypes.VIDEO) {
        return;
      }

      if (!workspaceFeatures.videoCover) {
        openUpgradeModal();
        return;
      }

      setEditThumbMediaFile({
        id,
        type,
        url,
        selectedTime: data?.thumbTime,
      });

      trackBasicEvent("post-editor", "click", "media - edit thumb");
    },
    [setEditThumbMediaFile, openUpgradeModal, workspaceFeatures]
  );

  const handleCloseEditThumbMediaFile = useCallback(
    () => setEditThumbMediaFile(null),
    [setEditThumbMediaFile]
  );

  const handleChangeThumbMediaFile = useCallback(
    ({ type, url }, thumbUrl, thumbTime) => {
      if (type !== PostEditorMediaTypes.VIDEO || !thumbUrl) {
        return;
      }

      const tabState =
        tabsState[activeTab] || getStateForTab(baseTabState, activeTab);
      const videos = (tabState?.videos || []).map((item) => {
        if (item.url === url) {
          return {
            ...item,
            thumbUrl,
            thumbTime,
          };
        }

        return item;
      });

      dispatchTabsState({
        type: "merge",
        tab: activeTab,
        payload: {
          videos,
        },
      });
    },
    [tabsState, baseTabState, activeTab]
  );

  const handleEditMediaFile = useCallback(
    ({ id, url }) => {
      setFilePickerType(PostEditorMediaSpotTypes.PHOTOS);
      setImageEditorFile({ id, url });
      setImageEditorOpen(true);

      trackBasicEvent("post-editor", "click", "image editor - edit file");
    },
    [setImageEditorOpen, setImageEditorFile, setFilePickerType]
  );

  const handleSaveImageEditor = useCallback(
    (name, data) => {
      const editFileUrl = imageEditorFile?.url;

      setImageEditorOpen(false);
      setImageEditorFile(null);

      if (editFileUrl) {
        handleRemoveMediaFile({ url: editFileUrl });
      }

      uppy.getPlugin("ImportUrlMedia")?.addFile(data, name, true);

      if (editFileUrl) {
        trackBasicEvent("post-editor", "click", "image editor - save (edit)");
      } else {
        trackBasicEvent("post-editor", "click", "image editor - save (new)");
      }
    },
    [
      setImageEditorOpen,
      setImageEditorFile,
      handleRemoveMediaFile,
      imageEditorFile,
      uppy,
    ]
  );

  const handleCloseImageEditor = useCallback(() => {
    setImageEditorOpen(false);
    setImageEditorFile(null);
    setFilePickerType(null);
  }, [setImageEditorOpen, setImageEditorFile, setFilePickerType]);

  const handlePreviewMediaFile = useCallback(
    ({ id, type, thumbUrl, url }) => {
      setPreviewMediaFile({
        id,
        type,
        thumbUrl,
        url,
      });

      trackBasicEvent("post-editor", "click", "media - preview");
    },
    [setPreviewMediaFile]
  );

  const handleMoveMediaFile = useCallback(
    ({ url, id }, value) => {
      if ((!url && !id) || (value !== 1 && value !== -1)) {
        return;
      }

      const items = getFixedPhotosVideosOrder(
        activeTabState.photos,
        activeTabState.videos
      );
      const moveItemOrder = [...items.videos, ...items.photos].find(
        (item) =>
          (item.url && item.url === url) ||
          (item.uploadId && item.uploadId === id)
      ).order;
      const newItemOrder = moveItemOrder + value;

      const photos = activeTabState.photos.map((item) => ({
        ...item,
        order:
          item.order === moveItemOrder
            ? newItemOrder
            : item.order === newItemOrder
              ? moveItemOrder
              : item.order,
      }));
      const videos = activeTabState.videos.map((item) => ({
        ...item,
        order:
          item.order === moveItemOrder
            ? newItemOrder
            : item.order === newItemOrder
              ? moveItemOrder
              : item.order,
      }));

      const itemsAfterMove = getFixedPhotosVideosOrder(photos, videos);

      dispatchTabsState({
        type: "merge",
        tab: activeTab,
        payload: {
          videos: itemsAfterMove.videos,
          photos: itemsAfterMove.photos,
        },
      });

      trackBasicEvent("post-editor", "click", "media - move");
    },
    [activeTab, activeTabState]
  );

  const handleClosePreviewMediaFile = useCallback(
    () => setPreviewMediaFile(null),
    [setPreviewMediaFile]
  );

  const addMediaFile = useCallback(
    (data) => {
      if (!data?.url || !data?.mimeType) {
        showToast({
          description: t("post_editor.media.errors.wrong_file_data"),
          title: "Error",
          status: "error",
        });

        return;
      }

      if (!data.external && quickMedia.find((item) => item.id === data.url)) {
        showToast({
          description: t("post_editor.media.errors.file_already_added"),
          title: "Error",
          status: "error",
        });

        return;
      }

      const baseMimeType = data.mimeType.split("/")[0];

      if (!["image", "video"].includes(baseMimeType)) {
        showToast({
          description: t("post_editor.media.errors.unsupported_file_type"),
          title: "Error",
          status: "error",
        });

        return;
      }

      const mimeExt = getExtension(data.mimeType);
      const url = new URL(data.url);
      const name = url.pathname.split("/").pop();
      const mediaType =
        baseMimeType === "video"
          ? PostEditorMediaSpotTypes.VIDEOS
          : PostEditorMediaSpotTypes.PHOTOS;

      if (
        activeTabConfig.maxPhotosVideos >= 0 &&
        (activeTabState?.videos?.length || 0) +
        (activeTabState?.photos?.length || 0) >=
        activeTabConfig.maxPhotosVideos
      ) {
        showToast({
          description: t("post_editor.errors.photo_video_limit_reached", {
            max: activeTabConfig.maxPhotosVideos,
          }),
          title: "Error",
          status: "error",
        });

        return;
      } else if (
        mediaType === PostEditorMediaSpotTypes.VIDEOS &&
        activeTabState?.videos?.length >= activeTabConfig.maxVideos
      ) {
        showToast({
          description: t("post_editor.errors.video_limit_reached", {
            max: activeTabConfig.maxVideos,
          }),
          title: "Error",
          status: "error",
        });

        return;
      } else if (
        mediaType === PostEditorMediaSpotTypes.PHOTOS &&
        activeTabState?.photos?.length >= activeTabConfig.maxPhotos
      ) {
        showToast({
          description: t("post_editor.errors.photo_limit_reached", {
            max: activeTabConfig.maxPhotos,
          }),
          title: "Error",
          status: "error",
        });

        return;
      }

      if (data.credits) {
        insertTextToMessage(data.credits);
      }

      setFilePickerType(mediaType);

      if (data.external) {
        uppy
          .getPlugin("ImportUrlMedia")
          ?.addFile(
            data.url,
            name.indexOf(".") > -1 ? name : `${name}.${mimeExt}`
          )
          ?.then((uppyFileId) => {
            const customFileData = {};

            if (data.converted) {
              customFileData.converted = true;
            }

            uppy.setFileState(uppyFileId, {
              customFileData,
            });
          });
      } else {
        setTimeout(() => {
          const customFileData = {};

          if (data.converted) {
            customFileData.converted = true;
          }

          const uploadedData = {
            url: data.url,
            thumbUrl: data.thumbUrl,
            dir: `${url.origin}${url.pathname.slice(
              0,
              url.pathname.lastIndexOf("/")
            )}`,
            fileName: name,
          };

          if (data.thumbTime !== undefined) {
            uploadedData.thumbTime = 0;
          }

          const uppyFileId = uppy.addFile({
            id: data.url,
            name,
            type: data.mimeType,
            data: {
              uploadedData,
            },
            preview: data.thumbUrl,
          });

          uppy.setFileState(uppyFileId, {
            customFileData,
          });

          const uppyFile = uppy.getFile(uppyFileId);

          uppy.emit("upload-success", uppyFile, {
            body: uppyFile.data.uploadedData,
          });

          // Prevent upload by others uploader
          uppy.setFileState(uppyFile.id, {
            progress: { uploadComplete: true, uploadStarted: true },
          });
        });
      }

      if (isSmallScreen) {
        showToast({
          description: t("post_editor.media.file_added"),
          status: "success",
        });
      }
    },
    [
      insertTextToMessage,
      setFilePickerType,
      t,
      isSmallScreen,
      quickMedia,
      uppy,
      activeTabConfig,
      activeTabState,
    ]
  );

  const handleAddFileFromMediaPanel = useCallback(
    (data) => {
      const baseMimeType = data.mimeType.split("/")[0];

      if (baseMimeType === "video" && !workspaceFeatures.video) {
        openUpgradeModal();
      } else {
        addMediaFile(data);
      }
    },
    [addMediaFile, openUpgradeModal, workspaceFeatures]
  );

  const handleAddTextFromAiAssistant = useCallback(
    (data) => {
      if (data?.payload?.headline) {
        insertTextToMessage(data.payload.headline);
      }
    },
    [insertTextToMessage]
  );

  const handleOpenEditComments = useCallback(() => {
    setEditComments(true);
  }, [setEditComments]);

  const handleCloseEditComments = useCallback(
    () => setEditComments(null),
    [setEditComments]
  );

  const handleSaveEditComments = useCallback(
    (comments) => {
      dispatchTabsState({
        type: "merge",
        tab: activeTab,
        payload: {
          comments: cloneDeep(comments || []),
        },
      });
    },
    [activeTab, dispatchTabsState]
  );

  const updateMessageLinks = useCallback(
    (useShortlinks) => {
      const view = messageRef.current?.view;

      if (!view) return;

      const text = view.state.doc.textBetween(
        0,
        view.state.doc.content.size,
        "\0",
        "\0"
      );
      const replaceItems = [];

      extractLinks(text).forEach((url) => {
        const correctUrl = getUrlWithProtocol(url);
        const shortLinkKey = Object.keys(shortlinksData).find(
          (k) => shortlinksData[k].short === correctUrl
        );
        const key = shortLinkKey || correctUrl;

        if (key && shortlinksData[key]) {
          if (
            useShortlinks &&
            shortlinksData[key].short &&
            !shortlinksData[key].disabled
          ) {
            replaceItems.push({
              from: url,
              to: shortlinksData[key].short,
            });
          } else if (shortlinksData[key].string) {
            replaceItems.push({
              from: url,
              to: shortlinksData[key].string,
            });
          }
        }
      });

      replaceLinks(view, replaceItems);
    },
    [shortlinksData, messageRef]
  );

  const updateScraperLink = useCallback(
    (useShortlinks) => {
      if (!activeTabState?.scraper?.link) {
        return;
      }

      const correctUrl = getUrlWithProtocol(activeTabState.scraper.link);
      const shortLinkKey = Object.keys(shortlinksData).find(
        (k) => shortlinksData[k].short === correctUrl
      );
      const key = shortLinkKey || correctUrl;

      if (key && shortlinksData[key]) {
        if (
          useShortlinks &&
          shortlinksData[key].short &&
          !shortlinksData[key].disabled
        ) {
          dispatchTabsState({
            type: "merge",
            tab: activeTab,
            payload: {
              scraper: {
                ...activeTabState.scraper,
                link: shortlinksData[key].short,
              },
            },
          });
        } else if (shortlinksData[key].string) {
          dispatchTabsState({
            type: "merge",
            tab: activeTab,
            payload: {
              scraper: {
                ...activeTabState.scraper,
                link: getUrlWithProtocol(shortlinksData[key].string),
              },
            },
          });
        }
      }
    },
    [shortlinksData, activeTabState, activeTab, dispatchTabsState]
  );

  const getLinksFromMessage = useCallback((ignoreCursorPosition) => {
    const view = messageRef.current?.view;

    if (!view) return null;

    const selection = view.state.selection.$anchor;

    if (
      !ignoreCursorPosition &&
      messageFocused &&
      ((selection.nodeBefore &&
        !/^\s$/.test(
          view.state.doc.textBetween(
            selection.pos - 1,
            selection.pos,
            "\0",
            "\0"
          )
        )) ||
        (selection.nodeAfter &&
          !/^\s$/.test(
            view.state.doc.textBetween(
              selection.pos,
              selection.pos + 1,
              "\0",
              "\0"
            )
          )))
    ) {
      return null;
    }

    // const text = view.state.doc.textBetween(0, view.state.doc.content.size, '\0', '\0');
    const text = activeTabState?.message || "";

    return extractLinks(text, false) || [];
  }, [messageFocused, activeTabState]);

  const fetchAndUpdateScraperData = useCallback(
    async (tab, link) => {
      const scraper = {
        link,
      };

      const data = await fetchScraperData(link);

      if (data) {
        scraper.title = data?.title || "";
        scraper.description = data?.description || "";
        scraper.image = data?.images?.[0] || "";

        data?.images?.forEach((itemUrl) => {
          if (!itemUrl) return;

          dispatchQuickMedia({
            type: "add",
            payload: {
              item: {
                id: itemUrl,
                type: PostEditorQuickMediaTypes.PHOTO_URL,
                url: itemUrl,
                thumbUrl: itemUrl,
              },
            },
          });
        });
      }

      dispatchTabsState({
        type: "merge",
        tab,
        payload: {
          scraper,
          _scraperLoading: false,
        },
      });
    },
    [fetchScraperData, dispatchTabsState, dispatchQuickMedia]
  );

  const clearScraper = useCallback(
    (tab, link) => {
      dispatchTabsState({
        type: "merge",
        tab,
        payload: {
          scraper: null,
          _scraperRemoved: link || null,
          _scraperLoading: false,
        },
      });
    },
    [dispatchTabsState]
  );

  const handleRemoveScraper = useCallback(() => {
    const link = activeTabState?.scraper?.link;

    if (!link) {
      return;
    }

    clearScraper(activeTab, link);

    trackBasicEvent("post-editor", "click", "scraper - remove");
  }, [clearScraper, activeTabState, activeTab]);

  const handleScraperTitleChange = useCallback(
    (value) => {
      dispatchTabsState({
        type: "merge",
        tab: activeTab,
        payload: {
          scraper: {
            ...activeTabState.scraper,
            title: value,
          },
        },
      });
    },
    [dispatchTabsState, activeTab, activeTabState]
  );

  const handleScraperDescriptionChange = useCallback(
    (value) => {
      dispatchTabsState({
        type: "merge",
        tab: activeTab,
        payload: {
          scraper: {
            ...activeTabState.scraper,
            description: value,
          },
        },
      });
    },
    [dispatchTabsState, activeTab, activeTabState]
  );

  const handleScraperAddImage = useCallback(() => {
    setFilePickerType(PostEditorMediaSpotTypes.LINK_PHOTO);
    setFilePickerOpen(true);
  }, [setFilePickerOpen, setFilePickerType]);

  const handleScraperChangeImage = useCallback(
    (image) => {
      dispatchTabsState({
        type: "merge",
        tab: activeTab,
        payload: {
          scraper: {
            ...activeTabState.scraper,
            image,
          },
        },
      });
    },
    [dispatchTabsState, activeTab, activeTabState]
  );

  const updateScraperFromMessage = useCallback((ignoreCursorPosition) => {
    if (
      !activeTabConfig.scraperEnabled ||
      activeTabState?.videos?.length ||
      activeTabState?.photos?.length ||
      activeTabState?.scraper?.link
    ) {
      return;
    }

    const links = getLinksFromMessage(ignoreCursorPosition);
    const correctFirstLink = links?.length
      ? getUrlWithProtocol(links[0])
      : null;

    if (
      correctFirstLink &&
      correctFirstLink !== activeTabState._scraperRemoved
    ) {
      dispatchTabsState({
        type: "merge",
        tab: activeTab,
        payload: {
          scraper: {
            link: correctFirstLink,
          },
          _scraperRemoved: null,
          _scraperLoading: true,
        },
      });

      fetchAndUpdateScraperData(activeTab, correctFirstLink);
    }
  }, [
    getLinksFromMessage,
    fetchAndUpdateScraperData,
    dispatchTabsState,
    activeTabConfig,
    activeTab,
    activeTabState,
  ]);

  const updateMessageLinkPreviewFromMessage = useCallback((ignoreCursorPosition) => {
    if (
      !activeTabConfig.messageLinkPreview ||
      activeTabState?.videos?.length ||
      activeTabState?.photos?.length ||
      activeTabState?.scraper?.link
    ) {
      if (messageLinkPreview) {
        setMessageLinkPreview(null);
      }

      return;
    }

    const links = getLinksFromMessage(ignoreCursorPosition);
    const correctLastLink = links?.length
      ? getUrlWithProtocol(links[links.length - 1])
      : null;

    if (correctLastLink && correctLastLink !== messageLinkPreview) {
      setMessageLinkPreview(correctLastLink);
    } else if (!correctLastLink) {
      const text = activeTabState?.message || "";
      const extractedLinks = extractLinks(text, false) || [];

      if (!extractedLinks.length) {
        setMessageLinkPreview(null);
      }
    }
  }, [
    getLinksFromMessage,
    setMessageLinkPreview,
    activeTabConfig,
    activeTabState,
    messageLinkPreview,
  ]);

  const getLinkData = useCallback(
    (range, text) => {
      if (!text) {
        return null;
      }

      const correctUrl = getUrlWithProtocol(text);
      const shortLinkKey = Object.keys(shortlinksData).find(
        (k) => shortlinksData[k].short === correctUrl
      );
      const key = shortLinkKey || correctUrl;

      if (key && shortlinksData[key]) {
        const linkData = shortlinksData[key];

        return {
          url: getUrlWithProtocol(linkData.string),
          short: linkData.short,
          disabled: linkData.disabled,
          hasShort: !!shortLinkKey,
        };
      }

      return {
        url: correctUrl,
      };
    },
    [shortlinksData]
  );

  const handleToggleLinkDisabling = useCallback(
    (range, text) => {
      if (!text || !workspaceFeatures.shortlinks) {
        return;
      }

      const correctUrl = getUrlWithProtocol(text);
      const shortLinkKey = Object.keys(shortlinksData).find(
        (k) => shortlinksData[k].short === correctUrl
      );
      const key = shortLinkKey || correctUrl;

      if (key && shortlinksData[key]) {
        setShortlinksData({
          ...shortlinksData,
          [key]: {
            ...shortlinksData[key],
            disabled: !shortlinksData[key]?.disabled,
          },
        });
      } else if (key) {
        setShortlinksData({
          ...shortlinksData,
          [key]: {
            string: text,
            disabled: !shortlinksData?.[key]?.disabled,
          },
        });
      }

      setReloadLinks(true);

      trackBasicEvent("post-editor", "click", "link disabling change");
    },
    [
      shortlinksData,
      workspaceFeatures.shortlinks,
      setShortlinksData,
      setReloadLinks,
    ]
  );

  const handleMessageChange = useCallback(
    ({ message, entitiesMap }) => {
      if (
        activeTab === PostEditorTabsTypes.BASE ||
        tabsState[activeTab] ||
        message !== baseTabState.message ||
        !isEqual(
          entitiesMap,
          getEntitiesMapForTab(baseTabState.entitiesMap, activeTab)
        )
      ) {
        dispatchTabsState({
          type: "merge",
          tab: activeTab,
          payload: {
            message,
            entitiesMap,
          },
        });

        updateScraperFromMessage();
        updateMessageLinkPreviewFromMessage();
      }
    },
    [
      dispatchTabsState,
      updateScraperFromMessage,
      updateMessageLinkPreviewFromMessage,
      baseTabState,
      tabsState,
      activeTab,
    ]
  );

  const handleMessageFocus = useCallback(() => {
    setMessageFocused(true);
  }, [setMessageFocused]);

  const handleMessageBlur = useCallback(() => {
    setMessageFocused(false);
  }, [setMessageFocused]);

  const handleMessagePaste = useCallback(() => {
    setMessagePasted(true);
  }, [setMessagePasted]);

  const handleOpenPostPreview = useCallback(() => {
    const postsByProvider = exportPostData();
    const postsAccountsIds = Object.values(postsByProvider).reduce(
      (ids, item) => [...ids, ...item.accounts],
      []
    );

    const data = {
      accounts: accounts.filter((item) => postsAccountsIds.includes(item.id)),
      postsByProvider,
    };

    setPostPreview(data);

    trackBasicEvent("post-editor", "click", "post preview - open");
  }, [accounts, exportPostData, setPostPreview]);

  const handleClosePostPreview = useCallback(
    () => setPostPreview(null),
    [setPostPreview]
  );

  const handleOpenSnapshotMaker = useCallback(() => {
    const postsByProvider = exportPostData();
    const postsAccountsIds = Object.values(postsByProvider).reduce((ids, item) => [...ids, ...item.accounts], []);

    const data = {
      accounts: accounts.filter((item) => postsAccountsIds.includes(item.id)),
      postsByProvider,
    };

    dispatch(setMediaSidebarPanelOpen(false));
    setSnapshotMaker(data);

    trackBasicEvent('post-editor', 'click', 'snapshot maker - open');
  }, [accounts, exportPostData, setSnapshotMaker, dispatch]);

  const handleCloseSnapshotMaker = useCallback(() => {
    setSnapshotMaker(null);
  }, [setSnapshotMaker]);

  const handleUseSnapshotMaker = useCallback(({ url, mimeType }) => {
    setSnapshotMaker(null);

    addMediaFile({
      url,
      thumbUrl: url,
      mimeType,
      external: true,
    });

    trackBasicEvent('post-editor', 'click', 'snapshot maker - use');
  }, [setSnapshotMaker, addMediaFile]);

  const handleTabChange = useCallback(
    (index) => {
      setActiveTab(activeTabs[index]);
      setReloadMessageEditor(true);

      trackBasicEvent("post-editor", "click", "editor tab change");
    },
    [setActiveTab, setReloadMessageEditor, activeTabs]
  );

  const handleResetActiveTabState = useCallback(() => {
    dispatchTabsState({
      type: "reset",
      tab: activeTab,
    });
    setReloadMessageEditor(true);

    trackBasicEvent("post-editor", "click", "reset active editor tab state");
  }, [dispatchTabsState, setReloadMessageEditor, activeTab]);

  const handleResetTabsState = useCallback(() => {
    Object.keys(tabsState)
      .filter((tab) => tab !== PostEditorTabsTypes.BASE)
      .forEach((tab) => {
        dispatchTabsState({
          type: "reset",
          tab,
        });
      });

    setReloadMessageEditor(true);

    trackBasicEvent("post-editor", "click", "reset all editor tabs state");
  }, [dispatchTabsState, setReloadMessageEditor, tabsState]);

  const handleCloseSaveConfirmModal = useCallback(
    () => setSaveConfirmOpen(null),
    [setSaveConfirmOpen]
  );

  const basicValidate = useCallback(
    (showTabWithError = false) => {
      if (!accountsIds?.length) {
        showToast({
          description: t("post_editor.errors.no_accounts_selected"),
          title: "Error",
          status: "error",
        });

        trackBasicEvent(
          "post-editor",
          "click",
          "basic validate - no accounts"
        );

        return false;
      }

      const noContentTab = activeTabs
        .filter((tab) => tab !== PostEditorTabsTypes.BASE)
        .find((tab) => {
          const tabData = tabsState[tab] || getStateForTab(baseTabState, tab);

          return (
            tabData &&
            !tabData?.message &&
            !tabData?.scraper?.link &&
            !tabData?.photos?.length &&
            !tabData?.videos?.length
          );
        });

      if (noContentTab) {
        showToast({
          description: t("post_editor.errors.no_content"),
          title: "Error",
          status: "error",
        });

        trackBasicEvent(
          "post-editor",
          "click",
          "basic validate - no content"
        );

        if (showTabWithError) {
          scrollToErrors();
          setActiveTab(noContentTab);
          setReloadMessageEditor(true);
        }

        return false;
      }

      return true;
    },
    [
      accountsIds,
      tabsState,
      baseTabState,
      activeTabs,
      scrollToErrors,
      setActiveTab,
      setReloadMessageEditor,
      t,
    ]
  );

  const postActionUpgradeRequired = useCallback(
    (action) => {
      switch (action) {
        case PostEditorActions.ADD_SCHEDULE: {
          if (!workspaceFeatures.canAddSchedule) {
            openUpgradeModal();
            return true;
          }
          break;
        }
        case PostEditorActions.ADD_BUFFER: {
          if (!workspaceFeatures.canAddBuffer) {
            openUpgradeModal();
            return true;
          }
          break;
        }
        case PostEditorActions.ADD_DRAFT:
        case PostEditorActions.ADD_FOR_APPROVAL_DRAFT: {
          if (!workspaceFeatures.canAddDraft) {
            openUpgradeModal();
            return true;
          }
          break;
        }
        default: {
          // nothing
        }
      }

      if (!workspaceFeatures.carousel || !workspaceFeatures.reelPost || !workspaceFeatures.storyPost) {
        const data = exportPostData();
        const tiktokData = data?.[PostEditorTabsTypes.TIKTOK];
        const instagramData = data?.[PostEditorTabsTypes.INSTAGRAM];
        const instagramPostType = instagramData?.additional?.find(
          (item) => item.type === PostAdditionalTypes.POST_TYPE
        )?.value;

        if (!workspaceFeatures.reelPost && (tiktokData || instagramPostType === PostTypes.REEL)) {
          openUpgradeModal(
            t("upgrade_modal.features.reel_post.title", {
              ns: "accounts",
            })
          );
          return true;
        }

        if (!workspaceFeatures.storyPost && instagramPostType === PostTypes.STORY) {
          openUpgradeModal(
            t("upgrade_modal.features.story_post.title", {
              ns: "accounts",
            })
          );
          return true;
        }

        const mediaCount =
          (instagramData?.photos?.length || 0) +
          (instagramData?.videos?.length || 0);

        if (!workspaceFeatures.carousel && mediaCount > 1) {
          openUpgradeModal(
            t("upgrade_modal.features.instagram_carousel.title", {
              ns: "accounts",
            })
          );
          return true;
        }
      }

      return false;
    },
    [openUpgradeModal, workspaceFeatures, exportPostData, t]
  );

  const handleSavePostClick = useCallback(async () => {
    if (postActionUpgradeRequired(PostEditorActions.SAVE)) {
      return;
    }

    if (!basicValidate(false)) {
      return;
    }

    const errorsByTab = await validateTabs();
    const errors = errorsByTab[activeTab]?.filter((item) => !item.level || item.level === PostEditorErrorLevels.ERROR);
    const hasErrors = !!errors?.length;

    if (hasErrors) {
      showToast({
        description: t("post_editor.check_errors"),
        title: "Error",
        status: "error",
      });

      scrollToErrors();

      trackBasicEvent(
        "post-editor",
        "click",
        `validate errors - ${PostEditorActions.SAVE}`
      );

      return;
    }

    setSavingInProgress(PostEditorActions.SAVE);

    const data = exportPostData();
    const saveData = {
      [activeTab]: {
        ...data[activeTab],
        id: postId,
      },
    };

    const onError = () => {
      trackBasicEvent(
        "post-editor",
        "click",
        `action error - ${PostEditorActions.SAVE}`
      );
    };

    const onSuccess = () => {
      onActionDone?.(PostEditorActions.SAVE, saveData);
      trackBasicEvent(
        "post-editor",
        "click",
        `action done - ${PostEditorActions.SAVE}`
      );
    };

    if (onCustomAction) {
      await onCustomAction?.(PostEditorActions.SAVE, saveData);
      onActionDone?.(PostEditorActions.SAVE, saveData);
    } else {
      try {
        const response = await dispatch(savePost(saveData));

        if (response?.data?.status === 1) {
          onSuccess();
        } else {
          onError();
        }
      } catch (error) {
        onError();
      }
    }

    setSavingInProgress(null);
  }, [
    postActionUpgradeRequired,
    basicValidate,
    validateTabs,
    scrollToErrors,
    setSavingInProgress,
    exportPostData,
    postId,
    activeTab,
    onActionDone,
    onCustomAction,
    dispatch,
    t,
  ]);

  const handleAddSchedulePostClick = useCallback(async () => {
    if (postActionUpgradeRequired(PostEditorActions.ADD_SCHEDULE)) {
      return;
    }

    const modalOpened = saveConfirmOpen === PostEditorActions.ADD_SCHEDULE;

    if (modalOpened) {
      setSaveConfirmOpen(null);
    }

    if (!basicValidate(true)) {
      return;
    }

    const errorsByTab = await validateTabs();
    const hasErrors = !!Object.values(errorsByTab)
      .map((errors) => errors.filter((item) => !item.level || item.level === PostEditorErrorLevels.ERROR))
      .filter((errors) => !!errors.length).length;

    if (hasErrors) {
      showToast({
        description: t("post_editor.check_errors"),
        title: "Error",
        status: "error",
      });

      scrollToErrors();
      setActiveTab(Object.keys(errorsByTab)[0]);
      setReloadMessageEditor(true);

      trackBasicEvent(
        "post-editor",
        "click",
        `validate errors - ${PostEditorActions.ADD_SCHEDULE}`
      );

      return;
    }

    if (!modalOpened) {
      setSaveConfirmOpen(PostEditorActions.ADD_SCHEDULE);
      return;
    }

    setSavingInProgress(PostEditorActions.ADD_SCHEDULE);

    const data = exportPostData({ addGroupId: true });

    const onError = () => {
      trackBasicEvent(
        "post-editor",
        "click",
        `action error - ${PostEditorActions.ADD_SCHEDULE}`
      );
    };

    const onSuccess = () => {
      onActionDone?.(PostEditorActions.ADD_SCHEDULE, data);
      trackBasicEvent(
        "post-editor",
        "click",
        `action done - ${PostEditorActions.ADD_SCHEDULE}`
      );
      analyticsTrackEvent("createdPost", {
        type: "scheduled",
        channels: Object.keys(data),
      });
    };

    try {
      const response = await dispatch(addSchedulePost(data));

      if (response?.data?.status === 1) {
        onSuccess();
      } else {
        onError();
      }
    } catch (error) {
      onError();
    }

    setSavingInProgress(null);
  }, [
    postActionUpgradeRequired,
    basicValidate,
    validateTabs,
    scrollToErrors,
    setSavingInProgress,
    exportPostData,
    setActiveTab,
    setReloadMessageEditor,
    setSaveConfirmOpen,
    saveConfirmOpen,
    onActionDone,
    dispatch,
    t,
  ]);

  const handleAddBufferPostClick = useCallback(
    async (acknowledged) => {
      if (postActionUpgradeRequired(PostEditorActions.ADD_BUFFER)) {
        return;
      }

      const modalOpened = saveConfirmOpen === PostEditorActions.ADD_BUFFER;

      if (modalOpened) {
        setSaveConfirmOpen(null);
      }

      if (!basicValidate(true)) {
        return;
      }

      const errorsByTab = await validateTabs();
      const hasErrors = !!Object.values(errorsByTab)
        .map((errors) => errors.filter((item) => !item.level || item.level === PostEditorErrorLevels.ERROR))
        .filter((errors) => !!errors.length).length;

      if (hasErrors) {
        showToast({
          description: t("post_editor.check_errors"),
          title: "Error",
          status: "error",
        });

        scrollToErrors();
        setActiveTab(Object.keys(errorsByTab)[0]);
        setReloadMessageEditor(true);

        trackBasicEvent(
          "post-editor",
          "click",
          `validate errors - ${PostEditorActions.ADD_BUFFER}`
        );

        return;
      }

      if (!modalOpened && !addToQueueAcknowledged) {
        setSaveConfirmOpen(PostEditorActions.ADD_BUFFER);
        return;
      }

      if (acknowledged === true) {
        setAddToQueueAcknowledged(true);
      }

      setSavingInProgress(PostEditorActions.ADD_BUFFER);

      const data = exportPostData({ addGroupId: true });
      const saveData = {};

      Object.keys(data).forEach((tab) => {
        saveData[tab] = {
          ...data[tab],
          timezone: defaultTimezone,
        };
      });

      const onError = () => {
        trackBasicEvent(
          "post-editor",
          "click",
          `action error - ${PostEditorActions.ADD_BUFFER}`
        );
      };

      const onSuccess = () => {
        onActionDone?.(PostEditorActions.ADD_BUFFER, saveData);
        trackBasicEvent(
          "post-editor",
          "click",
          `action done - ${PostEditorActions.ADD_BUFFER}`
        );
        analyticsTrackEvent("createdPost", {
          type: "queue",
          channels: Object.keys(saveData),
        });
      };

      try {
        const response = await dispatch(addBufferPost(saveData));

        if (response?.data?.status === 1) {
          onSuccess();
        } else {
          onError();
        }
      } catch (error) {
        onError();
      }

      setSavingInProgress(null);
    },
    [
      postActionUpgradeRequired,
      basicValidate,
      validateTabs,
      scrollToErrors,
      setSavingInProgress,
      exportPostData,
      setActiveTab,
      setReloadMessageEditor,
      setAddToQueueAcknowledged,
      addToQueueAcknowledged,
      setSaveConfirmOpen,
      saveConfirmOpen,
      onActionDone,
      defaultTimezone,
      dispatch,
      t,
    ]
  );

  const handlePublishNowPostClick = useCallback(async () => {
    if (postActionUpgradeRequired(PostEditorActions.PUBLISH_NOW)) {
      return;
    }

    const modalOpened = saveConfirmOpen === PostEditorActions.PUBLISH_NOW;

    if (modalOpened) {
      setSaveConfirmOpen(null);
    }

    if (!basicValidate(true)) {
      return;
    }

    const errorsByTab = await validateTabs();
    const hasErrors = !!Object.values(errorsByTab)
      .map((errors) => errors.filter((item) => !item.level || item.level === PostEditorErrorLevels.ERROR))
      .filter((errors) => !!errors.length).length;

    if (hasErrors) {
      showToast({
        description: t("post_editor.check_errors"),
        title: "Error",
        status: "error",
      });

      scrollToErrors();
      setActiveTab(Object.keys(errorsByTab)[0]);
      setReloadMessageEditor(true);

      trackBasicEvent(
        "post-editor",
        "click",
        `validate errors - ${PostEditorActions.PUBLISH_NOW}`
      );

      return;
    }

    if (!modalOpened) {
      setSaveConfirmOpen(PostEditorActions.PUBLISH_NOW);
      return;
    }

    setSavingInProgress(PostEditorActions.PUBLISH_NOW);

    const data = exportPostData({ addGroupId: true });

    Object.keys(data).forEach((tab) => {
      data[tab] = {
        ...data[tab],
        timezone: defaultTimezone,
      };
    });

    const onError = () => {
      trackBasicEvent(
        "post-editor",
        "click",
        `action error - ${PostEditorActions.PUBLISH_NOW}`
      );
    };

    const onSuccess = () => {
      onActionDone?.(PostEditorActions.PUBLISH_NOW, data);
      trackBasicEvent(
        "post-editor",
        "click",
        `action done - ${PostEditorActions.PUBLISH_NOW}`
      );
      analyticsTrackEvent("createdPost", {
        type: "now",
        channels: Object.keys(data),
      });
    };

    try {
      const response = await dispatch(addPublishNowPost(data));

      if (response?.data?.status === 1) {
        onSuccess();
      } else {
        onError();
      }
    } catch (error) {
      onError();
    }

    setSavingInProgress(null);
  }, [
    postActionUpgradeRequired,
    basicValidate,
    validateTabs,
    scrollToErrors,
    setSavingInProgress,
    exportPostData,
    setActiveTab,
    setReloadMessageEditor,
    setSaveConfirmOpen,
    saveConfirmOpen,
    onActionDone,
    defaultTimezone,
    dispatch,
    t,
  ]);

  const handleSaveDraftPostClick = useCallback(async () => {
    if (postActionUpgradeRequired(PostEditorActions.SAVE_DRAFT)) {
      return;
    }

    if (!basicValidate(false)) {
      return;
    }

    if (postData?.draftStatus && postData?.draftStatus !== "new") {
      const errorsByTab = await validateTabs();
      const errors = errorsByTab[activeTab]?.filter((item) => !item.level || item.level === PostEditorErrorLevels.ERROR);
      const hasErrors = !!errors?.length;

      if (hasErrors) {
        showToast({
          description: t("post_editor.check_errors"),
          title: "Error",
          status: "error",
        });

        scrollToErrors();

        trackBasicEvent(
          "post-editor",
          "click",
          `validate errors - ${PostEditorActions.SAVE_DRAFT}`
        );

        return;
      }
    }

    setSavingInProgress(PostEditorActions.SAVE_DRAFT);

    const data = exportPostData();
    const saveData = {
      [activeTab]: {
        ...data[activeTab],
        id: postId,
      },
    };

    const onError = () => {
      trackBasicEvent(
        "post-editor",
        "click",
        `action error - ${PostEditorActions.SAVE_DRAFT}`
      );
    };

    const onSuccess = () => {
      onActionDone?.(PostEditorActions.SAVE_DRAFT, saveData);
      trackBasicEvent(
        "post-editor",
        "click",
        `action done - ${PostEditorActions.SAVE_DRAFT}`
      );
    };

    try {
      const response = await dispatch(saveDraft(saveData));

      if (response?.data?.status === 1) {
        onSuccess();
      } else {
        onError();
      }
    } catch (error) {
      onError();
    }

    setSavingInProgress(null);
  }, [
    postActionUpgradeRequired,
    basicValidate,
    validateTabs,
    scrollToErrors,
    setSavingInProgress,
    exportPostData,
    postId,
    activeTab,
    postData,
    onActionDone,
    dispatch,
    t,
  ]);

  const handleAddDraftPostClick = useCallback(async () => {
    if (postActionUpgradeRequired(PostEditorActions.ADD_DRAFT)) {
      return;
    }

    const modalOpened = saveConfirmOpen === PostEditorActions.ADD_DRAFT;

    if (modalOpened) {
      setSaveConfirmOpen(null);
    }

    if (!basicValidate(true)) {
      return;
    }

    if (!modalOpened) {
      setSaveConfirmOpen(PostEditorActions.ADD_DRAFT);
      return;
    }

    setSavingInProgress(PostEditorActions.ADD_DRAFT);

    const data = exportPostData({ addGroupId: true });

    const onError = () => {
      trackBasicEvent(
        "post-editor",
        "click",
        `action error - ${PostEditorActions.ADD_DRAFT}`
      );
    };

    const onSuccess = () => {
      onActionDone?.(PostEditorActions.ADD_DRAFT, data);
      trackBasicEvent(
        "post-editor",
        "click",
        `action done - ${PostEditorActions.ADD_DRAFT}`
      );
    };

    try {
      const response = await dispatch(addDraft(data));

      if (response?.data?.status === 1) {
        onSuccess();
      } else {
        onError();
      }
    } catch (error) {
      onError();
    }

    setSavingInProgress(null);
  }, [
    postActionUpgradeRequired,
    basicValidate,
    setSavingInProgress,
    exportPostData,
    setSaveConfirmOpen,
    saveConfirmOpen,
    onActionDone,
    dispatch,
  ]);

  const handleAddForApprovalDraftPostClick = useCallback(async () => {
    if (postActionUpgradeRequired(PostEditorActions.ADD_FOR_APPROVAL_DRAFT)) {
      return;
    }

    const modalOpened =
      saveConfirmOpen === PostEditorActions.ADD_FOR_APPROVAL_DRAFT;

    if (modalOpened) {
      setSaveConfirmOpen(null);
    }

    if (!basicValidate(true)) {
      return;
    }

    const errorsByTab = await validateTabs();
    const hasErrors = !!Object.values(errorsByTab)
      .map((errors) => errors.filter((item) => !item.level || item.level === PostEditorErrorLevels.ERROR))
      .filter((errors) => !!errors.length).length;

    if (hasErrors) {
      showToast({
        description: t("post_editor.check_errors"),
        title: "Error",
        status: "error",
      });

      scrollToErrors();
      setActiveTab(Object.keys(errorsByTab)[0]);
      setReloadMessageEditor(true);

      trackBasicEvent(
        "post-editor",
        "click",
        `validate errors - ${PostEditorActions.ADD_FOR_APPROVAL_DRAFT}`
      );

      return;
    }

    if (!modalOpened) {
      setSaveConfirmOpen(PostEditorActions.ADD_FOR_APPROVAL_DRAFT);
      return;
    }

    setSavingInProgress(PostEditorActions.ADD_FOR_APPROVAL_DRAFT);

    const data = exportPostData({ addGroupId: true });

    const onError = () => {
      trackBasicEvent(
        "post-editor",
        "click",
        `action error - ${PostEditorActions.ADD_FOR_APPROVAL_DRAFT}`
      );
    };

    const onSuccess = () => {
      onActionDone?.(PostEditorActions.ADD_FOR_APPROVAL_DRAFT, data);
      trackBasicEvent(
        "post-editor",
        "click",
        `action done - ${PostEditorActions.ADD_FOR_APPROVAL_DRAFT}`
      );
    };

    try {
      const response = await dispatch(addForApprovalDraft(data));

      if (response?.data?.status === 1) {
        onSuccess();
      } else {
        onError();
      }
    } catch (error) {
      onError();
    }

    setSavingInProgress(null);
  }, [
    postActionUpgradeRequired,
    basicValidate,
    validateTabs,
    scrollToErrors,
    setSavingInProgress,
    exportPostData,
    setActiveTab,
    setReloadMessageEditor,
    setSaveConfirmOpen,
    saveConfirmOpen,
    onActionDone,
    dispatch,
    t,
  ]);

  const updateAdditionalValues = useCallback(
    (tab, type, values = {}) => {
      const tabState = tabsState[tab] || getStateForTab(baseTabState, tab);
      const data = tabState?.additional?.find(
        (item) => item.source === tab && item.type === type
      ) || {
        source: tab,
        type,
      };

      dispatchTabsState({
        type: "merge",
        tab,
        payload: {
          additional: [
            ...(tabState?.additional?.filter(
              (item) => !(item.source === tab && item.type === type)
            ) || []),
            {
              ...data,
              ...values,
            },
          ],
        },
      });
    },
    [tabsState, baseTabState, dispatchTabsState]
  );

  const updateAdditionalFieldValue = useCallback(
    (tab, type, field, value) => {
      if (field) {
        updateAdditionalValues(tab, type, {
          [field]: value,
        });
      }
    },
    [updateAdditionalValues]
  );

  const removeAdditional = useCallback(
    (tab, type) => {
      const tabState = tabsState[tab] || getStateForTab(baseTabState, tab);

      dispatchTabsState({
        type: "merge",
        tab,
        payload: {
          additional: [
            ...(tabState?.additional?.filter(
              (item) => item.source !== tab && item.type !== type
            ) || []),
          ],
        },
      });
    },
    [tabsState, baseTabState, dispatchTabsState]
  );

  const handleAdditionalPostInputFieldChange = useCallback(
    (field) => (event) => {
      updateAdditionalFieldValue(
        activeTab,
        PostAdditionalTypes.POST,
        field,
        event.target.value
      );
    },
    [activeTab, updateAdditionalFieldValue]
  );

  const handleAdditionalPostFieldChange = useCallback(
    (field) => (value) => {
      updateAdditionalFieldValue(
        activeTab,
        PostAdditionalTypes.POST,
        field,
        value
      );
    },
    [activeTab, updateAdditionalFieldValue]
  );

  const handleCreateCarousel = useCallback(() => {
    if (activeTabAdditional?.carousel) {
      return;
    }

    const items = [];

    if (activeTabState?.scraper?.link) {
      items.push({
        link: activeTabState?.scraper?.link,
        title: activeTabState?.scraper?.title,
        description: activeTabState?.scraper?.description,
        image: activeTabState?.scraper?.image,
      });
    } else {
      items.push({
        link: "",
      });
    }

    updateAdditionalFieldValue(
      activeTab,
      PostAdditionalTypes.CAROUSEL,
      "items",
      items
    );

    trackBasicEvent("post-editor", "click", "carousel - create");
  }, [
    activeTab,
    activeTabState,
    activeTabAdditional,
    updateAdditionalFieldValue,
  ]);

  const handleChangeCarousel = useCallback(
    (data = {}) => {
      if (data?.items && !data?.items?.length) {
        removeAdditional(activeTab, PostAdditionalTypes.CAROUSEL);
      } else {
        updateAdditionalValues(activeTab, PostAdditionalTypes.CAROUSEL, data);
      }
    },
    [activeTab, updateAdditionalValues, removeAdditional]
  );

  const handleRemoveCarousel = useCallback(() => {
    removeAdditional(activeTab, PostAdditionalTypes.CAROUSEL);
  }, [activeTab, removeAdditional]);

  useEffect(() => {
    setEditorData(postData, savedData);
  }, [postData]);

  useEffect(() => {
    if (addPostMedia?.length) {
      for (let i = 0; i < addPostMedia.length; i++) {
        addMediaFile(addPostMedia[i]);
      }
    }
  }, [addPostMedia]);

  useEffect(() => {
    setSavedData({
      quickMedia: cloneDeep(quickMedia),
      tabsState: cloneDeep(tabsState),
      shortlinksData: cloneDeep(shortlinksData),
    });
  }, [quickMedia, tabsState, shortlinksData]);

  useEffect(() => {
    if (reloadMessageEditor) {
      updateMessageEditor();
      validateTabs();
      setReloadMessageEditor(false);
    }
  }, [reloadMessageEditor]);

  useEffect(() => {
    if (reloadLinks) {
      updateMessageLinks(workspaceFeatures.shortlinks);
      updateScraperLink(workspaceFeatures.shortlinks);
      setReloadLinks(false);
    }
  }, [reloadLinks]);

  useEffect(() => {
    if (!messageFocused && messageFocused !== undefined) {
      updateScraperFromMessage();
      updateMessageLinkPreviewFromMessage();
      validateTabs();
    }
  }, [messageFocused]);

  useEffect(() => {
    if (!messageFocused || messagePasted) {
      setMessagePasted(false);
      updateScraperFromMessage(messagePasted);
      updateMessageLinkPreviewFromMessage(messagePasted);
      validateTabs();
    }
  }, [activeTabState]);

  useEffect(() => {
    if (singleMode) {
      const tabToSelect = activeTabs[1] || PostEditorTabsTypes.BASE;

      if (
        tabToSelect !== activeTab &&
        activeTab !== PostEditorTabsTypes.BASE &&
        tabsState[activeTab]
      ) {
        setEditorData(
          {},
          {
            tabsState: {
              [PostEditorTabsTypes.BASE]: cloneDeep(
                tabsState[activeTab] || {}
              ),
            },
            quickMedia: cloneDeep(quickMedia),
          }
        );
      }

      setActiveTab(tabToSelect);
      setReloadMessageEditor(true);
    } else if (!activeTabs.includes(activeTab)) {
      setActiveTab(PostEditorTabsTypes.BASE);
      setReloadMessageEditor(true);
    } else if (
      activeTabs?.length === 2 &&
      (isBaseTabEmpty(baseTabState) || prevActiveTabs?.length === 1)
    ) {
      const tabToSelect = activeTabs[1] || PostEditorTabsTypes.BASE;

      setActiveTab(tabToSelect);
      setReloadMessageEditor(true);
    } else if (
      prevActiveTabs?.length === 2 &&
      activeTabs?.length > 2 &&
      activeTab !== PostEditorTabsTypes.BASE &&
      isBaseTabEmpty(baseTabState)
    ) {
      if (tabsState[activeTab]) {
        setEditorData(
          {},
          {
            tabsState: {
              [PostEditorTabsTypes.BASE]: cloneDeep(
                tabsState[activeTab] || {}
              ),
              [activeTab]: cloneDeep(tabsState[activeTab] || {}), // @NOTE comment this line to move content from tab to base and clear customized data
            },
            quickMedia: cloneDeep(quickMedia),
          }
        );
      }

      setActiveTab(PostEditorTabsTypes.BASE);
      setReloadMessageEditor(true);
    }

    validateTabs();
  }, [activeTabs]);

  useEffect(() => {
    setActiveTabs([
      PostEditorTabsTypes.BASE,
      ...getTabsForAccounts(accounts, accountsIds),
    ]);
    validateTabs();
  }, [accounts, accountsIds]);

  useEffect(() => {
    const onUppyFilesAdded = (files) => {
      const ids = [];

      files.forEach((file) => {
        if (!uppy.getFile(file.id)) return;

        const type = isVideoFile(file)
          ? PostEditorQuickMediaTypes.VIDEO
          : PostEditorQuickMediaTypes.PHOTO;

        dispatchQuickMedia({
          type: "add",
          payload: {
            item: {
              id: file.id,
              type,
              progress: 0,
            },
          },
        });

        ids.push(file.id);
      });

      if (ids.length) {
        setUploadInProgress(true);
        setFilesToAdd(ids);
      }
    };

    const onUppyFileAdded = (file) => {
      // @NOTE additional file type verification
      if (!isPhotoFile(file) && !isVideoFile(file)) {
        uppy.removeFile(file.id);
      }
    };

    const onUppyFileRemoved = () => {
      if (!uppy.getFiles().length) {
        setUploadInProgress(false);
      }
    };

    const onUppyUploadProgress = (file, progress) => {
      const percentage = Math.max(
        Math.floor((progress.bytesUploaded / progress.bytesTotal) * 100),
        1
      );

      dispatchQuickMedia({
        type: "update",
        payload: {
          id: file.id,
          item: {
            progress: percentage,
          },
        },
      });
    };

    const onUppyUploadSuccess = async (file, { body }) => {
      const meta = await getMediaFileMeta(file.type.split("/")[0], body.url);

      dispatchQuickMedia({
        type: "update",
        payload: {
          id: file.id,
          item: {
            url: body.url,
            thumbUrl: body.thumbUrl,
            data: {
              ...file.customFileData,
              ...body,
              meta,
            },
            progress: null,
          },
        },
      });

      setLastUploadedFile(file.id);

      analyticsTrackEvent("uploadedMedia");

      uppy.removeFile(file.id);
    };

    const onUppyUploadError = (file) => {
      dispatchQuickMedia({
        type: "remove",
        payload: {
          id: file.id,
        },
      });

      uppy.removeFile(file.id);

      // @NOTE error message handled in onUppyInfoVisible
    };

    const onUppyThumbnailGenerated = (file, preview) => {
      dispatchQuickMedia({
        type: "update",
        payload: {
          id: file.id,
          item: {
            thumbUrl: preview,
          },
        },
      });
    };

    const onUppyVideoThumbnailGenerated = (file, preview) => {
      dispatchQuickMedia({
        type: "update",
        payload: {
          id: file.id,
          item: {
            thumbUrl: preview,
            thumbTime: null,
          },
        },
      });
    };

    const onUppyInfoVisible = () => {
      const { info } = uppy.getState();
      const uppyRestrictions = uppy.opts?.restrictions;

      if (uppyRestrictions?.allowedFileTypes?.length) {
        const items = [...info];
        const lastInfo = items.pop();
        const prevInfo = items.pop();

        if (
          lastInfo?.message &&
          lastInfo?.message !== prevInfo?.message &&
          ["error", "warning"].includes(lastInfo.type)
        ) {
          showToast({
            description: lastInfo.message,
            title: "Error",
            status: "error",
          });
        }
      }
    };

    uppy
      .on("files-added", onUppyFilesAdded)
      .on("file-added", onUppyFileAdded)
      .on("file-removed", onUppyFileRemoved)
      .on("upload-progress", onUppyUploadProgress)
      .on("upload-success", onUppyUploadSuccess)
      .on("upload-error", onUppyUploadError)
      .on("thumbnail:generated", onUppyThumbnailGenerated)
      .on("video-thumbnail:generated", onUppyVideoThumbnailGenerated)
      .on("info-visible", onUppyInfoVisible);

    return () => {
      uppy
        .off("files-added", onUppyFilesAdded)
        .off("file-added", onUppyFileAdded)
        .off("file-removed", onUppyFileRemoved)
        .off("upload-progress", onUppyUploadProgress)
        .off("upload-success", onUppyUploadSuccess)
        .off("upload-error", onUppyUploadError)
        .off("thumbnail:generated", onUppyThumbnailGenerated)
        .off("video-thumbnail:generated", onUppyVideoThumbnailGenerated)
        .off("info-visible", onUppyInfoVisible);
    };
  }, [uppy]);

  useEffect(() => {
    if (!uppy || !contentRef?.current) return;

    uppy.use(DropTargetHandler, {
      id: "DropTarget",
      target: contentRef.current,
      onDrop: (event, files) => {
        if (files.length) {
          setFilePickerType(
            isVideoFile(files[0])
              ? PostEditorMediaSpotTypes.VIDEOS
              : PostEditorMediaSpotTypes.PHOTOS
          );
          setDroppedFiles(files);
        }
      },
      onJsonDrop: (event, jsonData) => {
        if (jsonData?.from === "media-panel") {
          handleAddFileFromMediaPanel(jsonData);
        }
      },
    });
  }, [uppy]);

  useEffect(() => {
    if (filesToAdd) {
      addQuickMediaFiles(
        activeTab,
        filePickerType,
        filesToAdd,
        activeTabConfig.maxPhotosVideos >= 0
      );

      if (filePickerOpen) {
        setFilePickerOpen(false);
        setFilePickerType(null);
      }

      setFilesToAdd(null);
    }
  }, [filesToAdd]);

  useEffect(() => {
    if (lastUploadedFile) {
      updateUploadedFilesInTabsState();
      updateUploadedFilesIds();
    }
  }, [lastUploadedFile]);

  useEffect(() => {
    if (!uploadInProgress && uploadInProgress !== undefined) {
      updateUploadedFilesInTabsState(true);
      updateUploadedFilesIds();
      validateTabs();
    }
  }, [uploadInProgress]);

  useEffect(() => {
    const filesInProgress = uppy.getFiles().length;

    if (
      [
        PostEditorMediaSpotTypes.VIDEOS,
        PostEditorMediaSpotTypes.PHOTOS,
      ].includes(filePickerType)
    ) {
      const getMaxFiles = () => {
        if (activeTabConfig.maxPhotosVideos >= 0) {
          return (
            activeTabConfig.maxPhotosVideos -
            (activeTabState.videos.length +
              activeTabState.photos.length +
              filesInProgress)
          );
        } else if (filePickerType === PostEditorMediaSpotTypes.VIDEOS) {
          return (
            activeTabConfig.maxVideos -
            (activeTabState.videos.length + filesInProgress)
          );
        } else {
          return (
            activeTabConfig.maxPhotos -
            (activeTabState.photos.length + filesInProgress)
          );
        }
      };

      setUppyOptions({
        restrictions: {
          maxFileSize:
            filePickerType === PostEditorMediaSpotTypes.VIDEOS
              ? getVideoMaxSize()
              : getPhotoMaxSize(),
          maxNumberOfFiles: getMaxFiles(),
          allowedFileTypes:
            filePickerType === PostEditorMediaSpotTypes.VIDEOS
              ? getVideoAllowedFileTypes()
              : getPhotoAllowedFileTypes(),
        },
      });
    } else if (filePickerType === PostEditorMediaSpotTypes.LINK_PHOTO) {
      setUppyOptions({
        restrictions: {
          maxFileSize: getPhotoMaxSize(),
          maxNumberOfFiles: 1 + filesInProgress,
          allowedFileTypes: getPhotoAllowedFileTypes(),
        },
      });
    } else {
      setUppyOptions({
        restrictions: {
          maxFileSize: 0,
          maxNumberOfFiles: 0,
          allowedFileTypes: [],
        },
      });
    }
  }, [filePickerType]);

  useEffect(() => {
    if (droppedFiles?.length) {
      if (
        activeTabConfig.maxPhotosVideos >= 0 &&
        (activeTabState?.videos?.length || 0) +
        (activeTabState?.photos?.length || 0) >=
        activeTabConfig.maxPhotosVideos
      ) {
        setDroppedFiles(null);

        showToast({
          description: t("post_editor.errors.photo_video_limit_reached", {
            max: activeTabConfig.maxPhotosVideos,
          }),
          title: "Error",
          status: "error",
        });
      } else if (
        filePickerType === PostEditorMediaSpotTypes.VIDEOS &&
        activeTabState?.videos?.length >= activeTabConfig.maxVideos
      ) {
        setDroppedFiles(null);

        showToast({
          description: t("post_editor.errors.video_limit_reached", {
            max: activeTabConfig.maxVideos,
          }),
          title: "Error",
          status: "error",
        });
      } else if (
        filePickerType === PostEditorMediaSpotTypes.PHOTOS &&
        activeTabState?.photos?.length >= activeTabConfig.maxPhotos
      ) {
        setDroppedFiles(null);
        showToast({
          description: t("post_editor.errors.photo_limit_reached", {
            max: activeTabConfig.maxPhotos,
          }),
          title: "Error",
          status: "error",
        });
      } else {
        uppy.getPlugin("DropTarget")?.addFiles(droppedFiles);
        setDroppedFiles(null);
      }
    }
  }, [droppedFiles]);

  useEffect(() => {
    onLoading?.(loading || savingInProgress);
  }, [loading, savingInProgress]);

  useEffect(() => {
    if (mediaSidebarPanelOpen) {
      dispatch(closeAdvancedSettings());
    }
  }, [mediaSidebarPanelOpen]);

  useEffect(() => {
    if (rightSidebarPanel.isActionPanelOpen) {
      dispatch(setMediaSidebarPanelOpen(false));
    }
  }, [rightSidebarPanel.isActionPanelOpen]);

  useEffect(() => {
    setWorkspaceFeatures({
      shortlinks: false,
      video: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_VIDEO_POST
      ),
      videoCover: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_VIDEO_COVER
      ),
      carousel: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_CAROUSEL_POST
      ),
      reelPost: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_REEL_POST
      ),
      storyPost: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_STORY_POST
      ),
      comments: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_POST_COMMENTS
      ),
      snippets: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_POST_SNIPPETS
      ),
      mentions: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_POST_MENTIONS
      ),
      canAddSchedule: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_ADD_SCHEDULE
      ),
      canAddBuffer: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_ADD_TO_QUEUE
      ),
      canAddDraft: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_ADD_DRAFT
      ),
    });
  }, [subscriptionData]);

  useEffect(() => {
    trackBasicEvent("post-editor", "init", "editor loaded");
    trackBasicEvent("post-editor", "init", `editor mode: ${mode}`);

    return () => {
      dispatch(setMediaSidebarPanelOpen(false));
      dispatch(closeAdvancedSettings());
    };
  }, []);

  const getSelectedTextMenu = useCallback(({ text }) => {
    if (!text) {
      return null;
    }

    // @NOTE Due to the disabling of AI
    if (!appFeatures.snippets) {
      return null;
    }

    return (
      <ButtonGroup size="xs" variant="ghost">
        <Box display="flex" alignItems="center" flexWrap="wrap">
          {/*
          @NOTE Disabled, require changes for AI
          https://www.notion.so/sosimplified/Python-API-for-post-editor-for-AI-2cb806f6a7094e82b93dadb9506e459e
          */}
          {/* <Button
            onClick={() => {
              if (rightSidebarPanel.isActionPanelOpen && rightSidebarPanel.aiPromptId) {
                dispatch(updateDocumentData(rightSidebarPanel.aiPromptId, {
                  payload: {
                    title: text,
                    description: text,
                  },
                }));
              } else {
                dispatch(openNewAiDocumentInPanel()).then((responseData) => {
                  dispatch(updateDocumentData(responseData.id, {
                    payload: {
                      title: text,
                      description: text,
                    },
                  }));
                });
              }
              analyticsTrackEvent("plannerAIAssistantFromSelectedText");
            }}
          >
            {t("post_editor.text_toolbar.ai_assistant")}
          </Button> */}
          {appFeatures.snippets && (
            <>
              {/* <Divider
                orientation="vertical"
                height="1em"
                borderColor="whiteAlpha.100"
                margin="0"
              /> */}
              <Button
                onClick={() => {
                  if (!workspaceFeatures.snippets) {
                    openUpgradeModal();
                    return;
                  }

                  setSnippetData({
                    text,
                  });
                  setSnippetsOpen.on();
                }}
              >
                {t("post_editor.text_toolbar.create_snippet")}
              </Button>
            </>
          )}
        </Box>
      </ButtonGroup>
    );
  }, [t, setSnippetData, setSnippetsOpen, appFeatures.snippets, workspaceFeatures.snippets, openUpgradeModal]);

  const renderButtons = (modeParam) => {
    switch (modeParam) {
      case PostEditorModes.CREATE:
        return (
          <PostEditorCreatePostButtons
            isDisabled={loading || uploadInProgress}
            isLoading={savingInProgress}
            onAddSchedule={handleAddSchedulePostClick}
            onAddBuffer={handleAddBufferPostClick}
            canEditPosts={canEditPosts}
            onPublishNow={handlePublishNowPostClick}
            onAddDraft={handleAddDraftPostClick}
          />
        );
      case PostEditorModes.EDIT:
        return (
          <PostEditorEditPostButtons
            isDisabled={loading || uploadInProgress}
            isLoading={savingInProgress}
            date={timeState?.date}
            time={timeState?.time}
            timezone={timeState?.timezone}
            defaultTimezone={defaultTimezone}
            onSave={handleSavePostClick}
            onTimeChange={handleTimeChange}
          />
        );
      case PostEditorModes.CUSTOM_EDIT:
        return (
          <PostEditorEditPostButtons
            isDisabled={loading || uploadInProgress}
            isLoading={savingInProgress}
            hideTimeSelector={customHideTimeSelector}
            date={timeState?.date}
            time={timeState?.time}
            timezone={timeState?.timezone}
            defaultTimezone={defaultTimezone}
            onSave={handleSavePostClick}
            onTimeChange={handleTimeChange}
          />
        );
      case PostEditorModes.DRAFT_CREATE: {
        const account = accounts.find((item) => item.id === accountsIds[0]);

        return (
          <PostEditorCreateDraftPostButtons
            isDisabled={loading || uploadInProgress}
            isLoading={savingInProgress}
            onAddDraft={handleAddDraftPostClick}
            onAddForApprovalDraft={
              account?.workflowType === "advanced"
                ? handleAddForApprovalDraftPostClick
                : undefined
            }
          />
        );
      }
      case PostEditorModes.DRAFT_EDIT:
        return (
          <PostEditorEditDraftPostButtons
            isDisabled={loading || uploadInProgress}
            isLoading={savingInProgress}
            date={timeState?.date}
            time={timeState?.time}
            timezone={timeState?.timezone}
            canEditPosts={canEditPosts}
            defaultTimezone={defaultTimezone}
            onSave={handleSaveDraftPostClick}
            onTimeChange={handleTimeChange}
            onAddSchedule={handleAddSchedulePostClick}
            onAddBuffer={handleAddBufferPostClick}
            onPublishNow={handlePublishNowPostClick}
            onAddDraft={handleAddDraftPostClick}
          />
        );
      default:
        return null;
    }
  };

  const renderCommentsMessageCounter = useCallback(
    ({ state }) => (
      <>
        {activeTab !== PostEditorTabsTypes.TWITTER ? (
          <AdvancedMessageCharsCounter
            state={state}
            limit={activeTabConfig.maxMessageChars}
          />
        ) : null}
        {activeTab === PostEditorTabsTypes.TWITTER ||
          (activeTab === PostEditorTabsTypes.BASE &&
            activeTabs.includes(PostEditorTabsTypes.TWITTER) &&
            !tabsState[PostEditorTabsTypes.TWITTER]) ? (
          <AdvancedMessageCharsTwitterCounter
            state={state}
            limit={activeTabConfig.maxMessageCharsTwitter}
            leftIcon={FaTwitter}
          />
        ) : null}
      </>
    ),
    [activeTab, activeTabs, tabsState, activeTabConfig]
  );

  return (
    <Box sx={wrapperStyles} ref={editorRef}>
      {!singleMode ? (
        <Tabs
          isManual
          defaultIndex={0}
          index={activeTabIndex}
          onChange={handleTabChange}
          size="sm"
          colorScheme="primary"
        >
          <TabList borderColor="whiteAlpha.200" paddingX="1">
            {activeTabs.map((tab) => (
              <PostEditorTab
                key={tab}
                tab={tab}
                modified={
                  tab !== PostEditorTabsTypes.BASE && !!tabsState[tab]
                }
                error={
                  tab !== PostEditorTabsTypes.BASE && !!tabsErrors[tab]?.filter((error) => (!error.level || error.level === PostEditorErrorLevels.ERROR))?.length
                }
                hideText={isSmallScreen || activeTabs.length > 5}
              />
            ))}
          </TabList>
        </Tabs>
      ) : null}
      {activeTabAdditional[PostAdditionalTypes.POST_TYPE]?.value ? (
        <Box
          paddingY="2"
          paddingX="4"
        >
          <RadioGroup
            onChange={(value) => {
              updateAdditionalFieldValue(activeTab, PostAdditionalTypes.POST_TYPE, "value", value);
            }}
            value={activeTabAdditional[PostAdditionalTypes.POST_TYPE].value.value}
            variant="secondary"
          >
            <Stack direction="row" spacing="4">
              {activeTabAdditional[PostAdditionalTypes.POST_TYPE].value.values.map((item) => (
                <Radio
                  key={item}
                  value={item}
                  margin="0"
                >
                  <Box fontSize="sm" lineHeight="1">
                    {t(`post_editor.additional_fields.${activeTab}.${PostAdditionalTypes.POST_TYPE}.value.${item}`)}
                  </Box>
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </Box>
      ) : null}
      <Box
        sx={{ ...contentWrapperStyles, ...dropTargetStyles }}
        ref={contentRef}
      >
        {activeTabErrors?.length ? (
          <Box sx={errorsStyles}>
            {activeTabErrors.map((error) => (
              <PostEditorError
                key={error.id}
                message={t(`post_editor.errors.${error.id}`, error.values)}
                level={error.level}
              />
            ))}
          </Box>
        ) : null}
        {activeTabConfig.maxMessageChars !== 0 ? (
          <AdvancedMessageField
            ref={messageRef}
            sx={messageStyles}
            state={activeTabState}
            placeholder={t("post_editor.message.placeholder")}
            onChange={handleMessageChange}
            onFocus={handleMessageFocus}
            onBlur={handleMessageBlur}
            onPaste={handleMessagePaste}
            highlightLinks={activeTabConfig.highlightLinks}
            highlightHashtags={activeTabConfig.highlightHashtags}
            highlightAtMentions={activeTabConfig.highlightAtMentions}
            mentionEnabled={
              appFeatures.mentions &&
              workspaceFeatures.mentions &&
              activeTabConfig.mentionEnabled
            }
            mentionAllowSpaces={activeTabConfig.mentionAllowSpaces}
            mentionGetItems={handleMentionGetItems}
            onMentionSelect={handleMentionSelect}
            linkMenuEnabled={appFeatures.shortlinks}
            onToggleLinkDisabling={
              workspaceFeatures.shortlinks ? handleToggleLinkDisabling : null
            }
            getLinkData={getLinkData}
            selectedTextMenuEnabled
            getSelectedTextMenu={getSelectedTextMenu}
          />
        ) : null}
        {activeTab === PostEditorTabsTypes.GOOGLE && activeTabAdditional[PostAdditionalTypes.POST] ? (
          <Box sx={customFieldsStyles}>
            {activeTabAdditional[PostAdditionalTypes.POST].topicType ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.topicType`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Select
                    size="sm"
                    maxWidth="14rem"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].topicType
                        ?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange(
                      "topicType"
                    )}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].topicType
                        ?.isInvalid
                    }
                  >
                    {activeTabAdditional[
                      PostAdditionalTypes.POST
                    ].topicType?.values?.map((item) => (
                      <option key={item} value={item}>
                        {t(
                          `post_editor.additional_fields.${activeTab}.${[
                            PostAdditionalTypes.POST,
                          ]}.topicType.${item || "NONE"}`
                        )}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].title ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {`${t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.title`
                  )}*`}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Input
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].title
                        ?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange("title")}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].title
                        ?.isInvalid
                    }
                  />
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].startDate ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {`${t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.startDate`
                  )}*`}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Popover isLazy placement="top-start">
                    <PopoverTrigger>
                      <Input
                        size="sm"
                        maxWidth="11rem"
                        value={
                          activeTabAdditional[PostAdditionalTypes.POST]
                            .startDate?.value
                        }
                        isInvalid={
                          activeTabAdditional[PostAdditionalTypes.POST]
                            .startDate?.isInvalid
                        }
                        cursor="pointer"
                        isReadOnly
                      />
                    </PopoverTrigger>
                    <PopoverContent width="auto" padding="3" lineHeight="0">
                      <PopoverArrow />
                      <DateTimePicker
                        dateTime={
                          activeTabAdditional[PostAdditionalTypes.POST]
                            .startDate?.value
                        }
                        onChange={handleAdditionalPostFieldChange(
                          "startDate"
                        )}
                        showClearButton
                      />
                    </PopoverContent>
                  </Popover>
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].endDate ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {`${t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.endDate`
                  )}*`}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Popover isLazy placement="top-start">
                    <PopoverTrigger>
                      <Input
                        size="sm"
                        maxWidth="11rem"
                        value={
                          activeTabAdditional[PostAdditionalTypes.POST]
                            .endDate?.value
                        }
                        isInvalid={
                          activeTabAdditional[PostAdditionalTypes.POST]
                            .endDate?.isInvalid
                        }
                        cursor="pointer"
                        isReadOnly
                      />
                    </PopoverTrigger>
                    <PopoverContent width="auto" padding="3" lineHeight="0">
                      <PopoverArrow />
                      <DateTimePicker
                        dateTime={
                          activeTabAdditional[PostAdditionalTypes.POST]
                            .endDate?.value
                        }
                        onChange={handleAdditionalPostFieldChange("endDate")}
                        showClearButton
                      />
                    </PopoverContent>
                  </Popover>
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].couponCode ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.couponCode`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Input
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].couponCode
                        ?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange(
                      "couponCode"
                    )}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].couponCode
                        ?.isInvalid
                    }
                  />
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].redeemOnlineUrl ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.redeemOnlineUrl`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Input
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST]
                        .redeemOnlineUrl?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange(
                      "redeemOnlineUrl"
                    )}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST]
                        .redeemOnlineUrl?.isInvalid
                    }
                  />
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].termsConditions ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.termsConditions`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Textarea
                    size="sm"
                    resize="vertical"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST]
                        .termsConditions?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange(
                      "termsConditions"
                    )}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST]
                        .termsConditions?.isInvalid
                    }
                  />
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST]
              .callToActionType ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.callToActionType`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Select
                    size="sm"
                    width="33%"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST]
                        .callToActionType?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange(
                      "callToActionType"
                    )}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST]
                        .callToActionType?.isInvalid
                    }
                  >
                    {activeTabAdditional[
                      PostAdditionalTypes.POST
                    ].callToActionType?.values?.map((item) => (
                      <option key={item} value={item}>
                        {t(
                          `post_editor.additional_fields.${activeTab}.${[
                            PostAdditionalTypes.POST,
                          ]}.callToActionType.${item || "NONE"}`
                        )}
                      </option>
                    ))}
                  </Select>
                  {activeTabAdditional[PostAdditionalTypes.POST]
                    .callToActionUrl ? (
                    <Input
                      size="sm"
                      width="calc(67% - var(--chakra-space-1))"
                      marginLeft="1"
                      placeholder={t(
                        `post_editor.additional_fields.${activeTab}.${[
                          PostAdditionalTypes.POST,
                        ]}.callToActionUrl`
                      )}
                      value={
                        activeTabAdditional[PostAdditionalTypes.POST]
                          .callToActionUrl?.value
                      }
                      onChange={handleAdditionalPostInputFieldChange(
                        "callToActionUrl"
                      )}
                      isInvalid={
                        activeTabAdditional[PostAdditionalTypes.POST]
                          .callToActionUrl?.isInvalid
                      }
                    />
                  ) : null}
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : null}
        {activeTab === PostEditorTabsTypes.YOUTUBE && activeTabAdditional[PostAdditionalTypes.POST] ? (
          <Box sx={customFieldsStyles}>
            {activeTabAdditional[PostAdditionalTypes.POST].title ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {`${t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.title`
                  )}*`}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Input
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].title
                        ?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange("title")}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].title
                        ?.isInvalid
                    }
                  />
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].keywords ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.keywords`
                  )}
                  <Tooltip
                    label={
                      t(
                        `post_editor.additional_fields.${activeTab}.${[
                          PostAdditionalTypes.POST,
                        ]}.keywords.info`
                      )
                    }
                    placement="top"
                    hasArrow
                    shouldWrapChildren
                  >
                    <Icon as={FaInfoCircle} marginLeft="1" />
                  </Tooltip>
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Textarea
                    size="sm"
                    resize="vertical"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].keywords?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange("keywords")}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].keywords?.isInvalid
                    }
                  />
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].privacyStatus ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.privacyStatus`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Select
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].privacyStatus?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange("privacyStatus")}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].privacyStatus?.isInvalid
                    }
                  >
                    {activeTabAdditional[PostAdditionalTypes.POST].privacyStatus?.values?.map((item) => (
                      <option key={item} value={item}>
                        {t(
                          `post_editor.additional_fields.${activeTab}.${[PostAdditionalTypes.POST]}.privacyStatus.${item || "empty"}`
                        )}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].license ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.license`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Select
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].license?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange("license")}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].license?.isInvalid
                    }
                  >
                    {activeTabAdditional[PostAdditionalTypes.POST].license?.values?.map((item) => (
                      <option key={item} value={item}>
                        {t(
                          `post_editor.additional_fields.${activeTab}.${[PostAdditionalTypes.POST]}.license.${item || "empty"}`
                        )}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].selfDeclaredMadeForKids ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.selfDeclaredMadeForKids`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Select
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].selfDeclaredMadeForKids?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange("selfDeclaredMadeForKids")}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].selfDeclaredMadeForKids?.isInvalid
                    }
                  >
                    {activeTabAdditional[PostAdditionalTypes.POST].selfDeclaredMadeForKids?.values?.map((item) => (
                      <option key={item} value={item}>
                        {t(
                          `post_editor.additional_fields.${activeTab}.${[PostAdditionalTypes.POST]}.selfDeclaredMadeForKids.${item || "empty"}`
                        )}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : null}
        {activeTab === PostEditorTabsTypes.PINTEREST && activeTabAdditional[PostAdditionalTypes.POST] ? (
          <Box sx={customFieldsStyles}>
            {activeTabAdditional[PostAdditionalTypes.POST].title ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.title`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Input
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].title
                        ?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange("title")}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].title
                        ?.isInvalid
                    }
                  />
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].link ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.link`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Input
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].link
                        ?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange("link")}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].link
                        ?.isInvalid
                    }
                  />
                </Box>
              </Box>
            ) : null}
            {activeTabAdditional[PostAdditionalTypes.POST].imageAlt ? (
              <Box sx={customFieldRowStyles}>
                <Box sx={customFieldLabelStyles}>
                  {t(
                    `post_editor.additional_fields.${activeTab}.${[
                      PostAdditionalTypes.POST,
                    ]}.imageAlt`
                  )}
                </Box>
                <Box sx={customFieldContentStyles}>
                  <Input
                    size="sm"
                    value={
                      activeTabAdditional[PostAdditionalTypes.POST].imageAlt
                        ?.value
                    }
                    onChange={handleAdditionalPostInputFieldChange(
                      "imageAlt"
                    )}
                    isInvalid={
                      activeTabAdditional[PostAdditionalTypes.POST].imageAlt
                        ?.isInvalid
                    }
                  />
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : null}
        <Box sx={mediaStyles}>
          {activeTabConfig.messageLinkPreview && messageLinkPreview ? (
            <Box>
              <PostLinkPreview link={messageLinkPreview} autoFetch />
              <Box
                paddingTop="1"
                paddingBottom="1"
                fontSize="xs"
                color="yellow.500"
              >
                {t("post_editor.infos.twitter_link_preview")}
              </Box>
            </Box>
          ) : null}
          {activeTabConfig.scraperEnabled &&
            activeTabState?.scraper?.link &&
            (!activeTabConfig.carouselEnabled ||
              !activeTabAdditional?.carousel) ? (
            <Box position="relative">
              <PostLinkPreview
                link={activeTabState?.scraper?.link}
                title={activeTabState?.scraper?.title}
                description={activeTabState?.scraper?.description}
                image={
                  activeTabState._scraperImageUploadId
                    ? quickMedia.find(
                      (item) =>
                        item.id === activeTabState._scraperImageUploadId
                    )?.thumbUrl
                    : activeTabState?.scraper?.image
                }
                loading={
                  activeTabState._scraperLoading ||
                  !!activeTabState._scraperImageUploadId
                }
                onRemove={handleRemoveScraper}
                onTitleChange={
                  activeTabConfig.scraperAllowEditDetails
                    ? handleScraperTitleChange
                    : null
                }
                onDescriptionChange={
                  activeTabConfig.scraperAllowEditDetails
                    ? handleScraperDescriptionChange
                    : null
                }
                onImageAdd={
                  activeTabConfig.scraperAllowEditDetails
                    ? handleScraperAddImage
                    : null
                }
                onImageChange={
                  activeTabConfig.scraperAllowEditDetails
                    ? handleScraperChangeImage
                    : null
                }
              />
              {appFeatures.carousel && activeTabConfig.carouselEnabled ? (
                <AdvancedMessageToolbarButton
                  tooltipLabel={t("post_editor.toolbar.create_carousel")}
                  icon={FaStream}
                  onClick={handleCreateCarousel}
                  upgradeRequired={!workspaceFeatures.carousel}
                  background="gray.500"
                  color="white"
                  borderRadius="full"
                  position="absolute"
                  top="1"
                  left="1"
                  data-onboarding="post-editor-carousel"
                  sx={{
                    _hover: {
                      background: "black",
                    },
                  }}
                />
              ) : null}
            </Box>
          ) : null}
          {activeTabConfig.carouselEnabled &&
            activeTabAdditional?.carousel ? (
            <Box marginX="-1" paddingX="2" paddingY="1" background="gray.500">
              <PostCarousel
                data={activeTabAdditional.carousel}
                limit={activeTabConfig.carouselMaxItems}
                onChange={handleChangeCarousel}
                onRemove={handleRemoveCarousel}
              />
            </Box>
          ) : null}
          {activeTabMedia.length ? (
            <PostMediaList>
              {activeTabMedia.map((item) => (
                <PostMediaListItem
                  key={item.id}
                  id={item.id}
                  url={item.url}
                  thumbUrl={item.thumbUrl}
                  data={item.data}
                  type={
                    item.type === PostEditorQuickMediaTypes.VIDEO
                      ? PostEditorMediaTypes.VIDEO
                      : PostEditorMediaTypes.PHOTO
                  }
                  src={item.thumbUrl}
                  progress={item.progress}
                  duration={item.data?.meta?.duration}
                  errors={activeTabErrors
                    ?.filter(
                      (error) =>
                        ["photo", "video"].includes(error.context) &&
                        error?.items?.includes(item.url) &&
                        (!error.level || error.level === PostEditorErrorLevels.ERROR)
                    )
                    ?.map((error) =>
                      t(`post_editor.errors.${error.id}`, error.values)
                    )}
                  onRemove={handleRemoveMediaFile}
                  onEditThumb={
                    item.type === PostEditorQuickMediaTypes.VIDEO &&
                      !isQuickMediaFileInProgress(item)
                      ? handleEditThumbMediaFile
                      : null
                  }
                  onClick={
                    !isQuickMediaFileInProgress(item)
                      ? handlePreviewMediaFile
                      : null
                  }
                  onMove={handleMoveMediaFile}
                  removeTooltip={t(
                    "post_media_list.actions.remove_from_post"
                  )}
                  clickTooltip={t("post_media_list.actions.show_preview")}
                />
              ))}
            </PostMediaList>
          ) : null}
        </Box>
        <Box sx={toolbarStyles}>
          <Stack isInline align="center" spacing="1" marginLeft="auto">
            {activeTabConfig.maxMessageChars !== 0 ? (
              <>
                {activeTab !== PostEditorTabsTypes.TWITTER ? (
                  <AdvancedMessageCharsCounter
                    state={activeTabState}
                    limit={activeTabConfig.maxMessageChars}
                  />
                ) : null}
                {activeTab === PostEditorTabsTypes.TWITTER ||
                  (activeTab === PostEditorTabsTypes.BASE &&
                    activeTabs.includes(PostEditorTabsTypes.TWITTER) &&
                    !tabsState[PostEditorTabsTypes.TWITTER]) ? (
                  <AdvancedMessageCharsTwitterCounter
                    state={activeTabState}
                    limit={activeTabConfig.maxMessageCharsTwitter}
                    leftIcon={FaTwitter}
                  />
                ) : null}
                {activeTabConfig.highlightHashtags ? (
                  <AdvancedMessageHashtagsCounter
                    state={activeTabState}
                    limit={activeTabConfig.maxHashtags}
                    leftIcon={FaHashtag}
                  />
                ) : null}
                {activeTabConfig.highlightAtMentions ? (
                  <AdvancedMessageAtMentionsCounter
                    state={activeTabState}
                    limit={activeTabConfig.maxMentions}
                    leftIcon={FaAt}
                  />
                ) : null}
                {activeTabConfig.mentionEnabled &&
                  !activeTabConfig.highlightAtMentions ? (
                  <AdvancedMessageMentionsCounter
                    state={activeTabState}
                    limit={activeTabConfig.maxMentions}
                    leftIcon={FaAt}
                  />
                ) : null}
              </>
            ) : null}
            {singleMode ? null : (
              <>
                {activeTabConfig.maxMessageChars !== 0 ? (
                  <Divider
                    orientation="vertical"
                    height="1em"
                    borderColor="whiteAlpha.100"
                  />
                ) : null}
                {activeTab === PostEditorTabsTypes.BASE ? (
                  <ConfirmDangerButton
                    as={AdvancedMessageToolbarButton}
                    tooltipLabel={t(
                      "post_editor.reset_all_posts_state.title"
                    )}
                    icon={FaUndo}
                    isDisabled={Object.keys(tabsState).length === 1}
                    confirmTitle={t(
                      "post_editor.reset_all_posts_state.confirm_modal.title"
                    )}
                    confirmMessage={t(
                      "post_editor.reset_all_posts_state.confirm_modal.message"
                    )}
                    confirmButtonText={t(
                      "post_editor.reset_all_posts_state.confirm_modal.confirm"
                    )}
                    onConfirm={handleResetTabsState}
                  />
                ) : (
                  <ConfirmDangerButton
                    as={AdvancedMessageToolbarButton}
                    tooltipLabel={t("post_editor.reset_post_state.title")}
                    icon={FaUndo}
                    isDisabled={!tabsState[activeTab]}
                    confirmTitle={t(
                      "post_editor.reset_post_state.confirm_modal.title"
                    )}
                    confirmMessage={t(
                      "post_editor.reset_post_state.confirm_modal.message"
                    )}
                    confirmButtonText={t(
                      "post_editor.reset_post_state.confirm_modal.confirm"
                    )}
                    onConfirm={handleResetActiveTabState}
                  />
                )}
              </>
            )}
          </Stack>
          <Stack isInline align="center" spacing="1">
            {activeTabConfig.maxPhotos !== 0 ||
              (appFeatures.video && activeTabConfig.maxVideos !== 0) ? (
              <>
                <Menu
                  placement="top-start"
                  autoSelect={false}
                  isLazy
                  onClose={() => {
                    // hacky fix focus and tooltip position issue
                    setTimeout(() => {
                      if (addMediaButtonRef.current) {
                        addMediaButtonRef.current.blur();
                        addMediaButtonRef.current.dispatchEvent(new MouseEvent('mouseleave', { 'bubbles': true }));
                      }
                    });
                  }}
                >
                  <MenuButton
                    as={AdvancedMessageToolbarButton}
                    tooltipLabel={t("post_editor.toolbar.add_media")}
                    icon={FaPlus}
                    ref={addMediaButtonRef}
                  />
                  <MenuList fontSize="sm" minWidth="0">
                    <MenuItem
                      icon={<Icon as={FaFolder} fontSize="md" />}
                      onClick={handleMediaSidebarPanelToggle}
                    >
                      {t("post_editor.toolbar.media_library")}
                    </MenuItem>
                    {activeTabConfig.maxPhotos > 0 ? (
                      <MenuItem
                        icon={<Icon as={FaImage} fontSize="md" />}
                        onClick={handleAddImages}
                      >
                        {t("post_editor.toolbar.add_image")}
                      </MenuItem>
                    ) : null}
                    {appFeatures.video && activeTabConfig.maxVideos > 0 ? (
                      <MenuItem
                        icon={<Icon as={FaVideo} fontSize="md" />}
                        onClick={handleAddVideos}
                      >
                        {t("post_editor.toolbar.add_video")}
                      </MenuItem>
                    ) : null}
                  </MenuList>
                </Menu>
                <AdvancedMessageToolbarButton
                  tooltipLabel={t("post_editor.toolbar.media_clipboard")}
                  icon={FaPaperclip}
                  onClick={handleMediaClipboardToggle}
                  isDisabled={!activeTabQuickMedia?.length}
                  isActive={
                    showMediaClipboard && !!activeTabQuickMedia?.length
                  }
                />
              </>
            ) : null}
            {!hideImageEditor && activeTabConfig.maxPhotos !== 0 ? (
              <AdvancedMessageToolbarButton
                tooltipLabel={t("post_editor.toolbar.create_image")}
                icon={FaPaintBrush}
                onClick={handleAddPhotoWithImageEditor}
              />
            ) : null}
            {appFeatures.comments && activeTabConfig.maxComments !== 0 ? (
              <AdvancedMessageToolbarButton
                tooltipLabel={t("post_editor.toolbar.comments")}
                icon={FaComments}
                isActive={!!activeTabState?.comments?.length}
                count={activeTabState?.comments?.length}
                onClick={handleOpenEditComments}
                upgradeRequired={!workspaceFeatures.comments}
                data-onboarding="post-editor-comments"
              />
            ) : null}
            {activeTabConfig.maxMessageChars !== 0 ? (
              <>

                <Divider
                  orientation="vertical"
                  height="1em"
                  borderColor="whiteAlpha.100"
                />
                <AdvancedMessageToolbarButton
                  tooltipLabel={t("post_editor.toolbar.ai_assistant")}
                  icon={FaRobot}
                  onClick={handleAiAssistantPanelToggle}
                />
                {appFeatures.snippets ? (
                  <Popover
                    isOpen={snippetsOpen}
                    onOpen={setSnippetsOpen.on}
                    onClose={() => {
                      setSnippetsOpen.off();
                      setSnippetData();
                    }}
                    isLazy
                    placement="bottom-start"
                    bg="white"
                  >
                    <PopoverTrigger>
                      <AdvancedMessageToolbarButton
                        tooltipLabel={t("post_editor.toolbar.snippets")}
                        icon={FaScroll}
                        upgradeRequired={!workspaceFeatures.snippets}
                      />
                    </PopoverTrigger>
                    <PopoverContent sx={{ background: "gray.600" }}>
                      <SnippetsPicker onSelect={handleSnippetSelect} snippetData={snippetData} />
                    </PopoverContent>
                  </Popover>
                ) : null}
                <Popover isLazy placement="bottom-start">
                  <PopoverTrigger>
                    <AdvancedMessageToolbarButton
                      tooltipLabel={t("post_editor.toolbar.emoji")}
                      icon={FaSmile}
                    />
                  </PopoverTrigger>
                  <PopoverContent
                    width="auto"
                    lineHeight="0"
                    sx={{ background: "gray.600" }}
                  >
                    <EmojiPicker onSelect={handleEmojiSelect} />
                  </PopoverContent>
                </Popover>
              </>
            ) : null}
            <Divider
              orientation="vertical"
              height="1em"
              borderColor="whiteAlpha.100"
            />
            {activeTabConfig.maxPhotos > 0 ? (
              <AdvancedMessageToolbarButton
                tooltipLabel= {t("post_editor.toolbar.make_snapshot")}
                icon={FaCameraRetro}
                onClick={handleOpenSnapshotMaker}
                isDisabled={!accountsIds.length}
              />
            ): null}
            <AdvancedMessageToolbarButton
              tooltipLabel={t("post_editor.toolbar.post_preview")}
              icon={FaEye}
              onClick={handleOpenPostPreview}
              isDisabled={!accountsIds.length}
            />
          </Stack>
        </Box>
        {showMediaClipboard && activeTabQuickMedia?.length ? (
          <Box paddingX="2" paddingY="1">
            <PostMediaList size="sm">
              {activeTabQuickMedia.map((item) => (
                <PostMediaListItem
                  key={item.id}
                  id={item.id}
                  url={item.url}
                  thumbUrl={item.thumbUrl}
                  type={
                    item.type === PostEditorQuickMediaTypes.VIDEO
                      ? PostEditorMediaTypes.VIDEO
                      : PostEditorMediaTypes.PHOTO
                  }
                  src={item.thumbUrl}
                  progress={item.progress}
                  duration={item.data?.meta?.duration}
                  onRemove={handleRemoveQuickMediaFile}
                  onAdd={handleUseQuickMediaFile}
                  onClick={
                    !isQuickMediaFileInProgress(item)
                      ? handlePreviewMediaFile
                      : null
                  }
                  removeTooltip={t(
                    "post_media_list.actions.remove_from_media_clipboard"
                  )}
                  clickTooltip={t("post_media_list.actions.show_preview")}
                />
              ))}
            </PostMediaList>
          </Box>
        ) : null}
        {loading || savingInProgress ? (
          <PreloaderWrapper styles={{ zIndex: 3 }}>
            <Preloader />
          </PreloaderWrapper>
        ) : null}
      </Box>
      <Box
        display="flex"
        justifyContent="flex-end"
        marginX="3"
        marginBottom="1"
      >
        {canEditDrafts ? (
          renderButtons(mode)
        ) : (
          <Tooltip
            label={t("no_post_edit_permission", { ns: "common" })}
            placement="bottom"
            hasArrow
            textAlign="center"
            shouldWrapChildren
          >
            <Box pointerEvents="none">{renderButtons(mode)}</Box>
          </Tooltip>
        )}
      </Box>
      {filePickerOpen && filePickerType ? (
        <PostFilePickerModal
          onClose={handleCloseFilePicker}
          uppy={uppy}
          plugins={uppyPlugins}
          note={uppyNote}
          disableInformer
        />
      ) : null}
      {/* {imageEditorOpen && filePickerType ? ( // @NOTE Image editor disabled
        <ImageEditorModal
          image={imageEditorFile?.url}
          onClose={handleCloseImageEditor}
          onSave={handleSaveImageEditor}
        />
      ) : null} */}
      {editComments ? (
        <PostEditCommentsModal
          onClose={handleCloseEditComments}
          onSave={handleSaveEditComments}
          comments={activeTabState?.comments}
          limit={activeTabConfig.maxComments}
          // @NOTE currently, tea same configuration as for the post
          messageCounter={renderCommentsMessageCounter}
          highlightLinks={activeTabConfig.highlightLinks}
          highlightHashtags={activeTabConfig.highlightHashtags}
          highlightAtMentions={activeTabConfig.highlightAtMentions}
        // linkMenuEnabled={appFeatures.shortlinks}
        // onToggleLinkDisabling={workspaceFeatures.shortlinks ? handleToggleLinkDisabling : null}
        // getLinkData={getLinkData}
        />
      ) : null}
      {previewMediaFile ? (
        <PostMediaPreviewModal
          onClose={handleClosePreviewMediaFile}
          item={previewMediaFile}
        />
      ) : null}
      {editThumbMediaFile ? (
        <PostMediaEditThumbModal
          onClose={handleCloseEditThumbMediaFile}
          onSelect={handleChangeThumbMediaFile}
          item={editThumbMediaFile}
        />
      ) : null}
      {postPreview ? (
        <PostPreviewModal
          onClose={handleClosePostPreview}
          accounts={postPreview?.accounts}
          postsByProvider={postPreview?.postsByProvider}
        />
      ) : null}
      {snapshotMaker ? (
        <SnapshotMakerModal
          onClose={handleCloseSnapshotMaker}
          onUse={handleUseSnapshotMaker}
          accounts={snapshotMaker?.accounts}
          postsByProvider={snapshotMaker?.postsByProvider}
        />
      ) : null}
      {saveConfirmOpen === PostEditorActions.ADD_DRAFT ? (
        <PostEditorAddDraftTimePickerModal
          onClose={handleCloseSaveConfirmModal}
          onConfirm={handleAddDraftPostClick}
          accounts={accounts}
          accountsIds={accountsIds}
          customizedTimes={customizedTimesState}
          delayTime={delayTimeState}
          defaultTimezone={defaultTimezone}
          date={timeState?.date}
          time={timeState?.time}
          timezone={timeState?.timezone}
          onTimeChange={handleTimeChange}
          onDelayTimeChange={handleDelayTimeChange}
          onCustomizedTimesChange={handleCustomizedTimesChange}
        />
      ) : null}
      {saveConfirmOpen === PostEditorActions.ADD_FOR_APPROVAL_DRAFT ? (
        <PostEditorAddDraftTimePickerModal
          onClose={handleCloseSaveConfirmModal}
          onConfirm={handleAddForApprovalDraftPostClick}
          accounts={accounts}
          accountsIds={accountsIds}
          customizedTimes={customizedTimesState}
          delayTime={delayTimeState}
          defaultTimezone={defaultTimezone}
          date={timeState?.date}
          time={timeState?.time}
          timezone={timeState?.timezone}
          onTimeChange={handleTimeChange}
          onDelayTimeChange={handleDelayTimeChange}
          onCustomizedTimesChange={handleCustomizedTimesChange}
        />
      ) : null}
      {saveConfirmOpen === PostEditorActions.ADD_SCHEDULE ? (
        <PostEditorScheduleTimePickerModal
          onClose={handleCloseSaveConfirmModal}
          onConfirm={handleAddSchedulePostClick}
          accounts={accounts}
          accountsIds={accountsIds}
          customizedTimes={customizedTimesState}
          delayTime={delayTimeState}
          defaultTimezone={defaultTimezone}
          date={timeState?.date}
          time={timeState?.time}
          timezone={timeState?.timezone}
          onTimeChange={handleTimeChange}
          onDelayTimeChange={handleDelayTimeChange}
          onCustomizedTimesChange={handleCustomizedTimesChange}
        />
      ) : null}
      {saveConfirmOpen === PostEditorActions.PUBLISH_NOW ? (
        <PostEditorPublishNowConfirmModal
          onClose={handleCloseSaveConfirmModal}
          onConfirm={handlePublishNowPostClick}
        />
      ) : null}
      {saveConfirmOpen === PostEditorActions.ADD_BUFFER && (
        <PostEditorAddToQueueModal
          loading={savingInProgress === PostEditorActions.ADD_BUFFER}
          onClose={handleCloseSaveConfirmModal}
          onConfirm={handleAddBufferPostClick}
        />
      )}
      {mediaSidebarPanelOpen
        ? sidebarPortal(
          <Box position="fixed" top="0" bottom="0">
            <TldrMediaSidebarPanel
              onAddFile={handleAddFileFromMediaPanel}
            />
          </Box>
        )
        : null}
      {rightSidebarPanel.isActionPanelOpen
        ? sidebarPortal(
          <StudioBaseRightSideBar
            title={rightSidebarPanel.sliderPanelType}
            top={0}
            heightOffset={0}
          >
            <TldrStudioAiPanel
              onAddText={handleAddTextFromAiAssistant}
              renderlocation="calendar"
            ></TldrStudioAiPanel>
          </StudioBaseRightSideBar>
        )
        : null}
      {upgradeModal?.open ? (
        <TldrUpgradeSubscriptionModal
          show
          onHide={closeUpgradeModal}
          title={upgradeModal?.title}
        />
      ) : null}
    </Box>
  );
};

PostEditor.propTypes = {
  accounts: PropTypes.arrayOf(SocialAccountPropType).isRequired,
  accountsIds: PropTypes.arrayOf(PropTypes.string.isRequired),
  canEditDrafts: PropTypes.bool.isRequired,
  canEditPosts: PropTypes.bool.isRequired,
  postData: PostPropType,
  addPostMedia: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      thumbUrl: PropTypes.string,
      mimeType: PropTypes.string.isRequired,
      external: PropTypes.bool,
      converted: PropTypes.bool,
    }),
  ),
  mode: PropTypes.oneOf(Object.values(PostEditorModes)),
  dataStoreKey: PropTypes.string,
  customHideTimeSelector: PropTypes.bool,
  onLoading: PropTypes.func,
  onActionDone: PropTypes.func,
  onCustomAction: PropTypes.func,
};

PostEditor.defaultProps = {
  accountsIds: undefined,
  canEditDrafts: true,
  canEditPosts: true,
  postData: undefined,
  addPostMedia: undefined,
  mode: undefined,
  dataStoreKey: undefined,
  customHideTimeSelector: undefined,
  onLoading: undefined,
  onActionDone: undefined,
  onCustomAction: undefined,
};

export default PostEditor;
